<template>
  <div>
    <feature-toggle :toggle="selectCompetitorsOnExport">
      <select-competitors-download-dialog
        :is-open="isSelectCompetitorsDialogOpen"
        @closeSelectCompetitorDialog="isSelectCompetitorsDialogOpen = false"
        @downloadData="downloadDataWithSelection($event)"
      />
    </feature-toggle>
    <v-row class="mb-2 controls-row" justify="start" align="center">
      <v-col cols="2" class="ml-4 pr-2">
        <v-text-field
          v-model="searchString"
          flat
          dense
          hide-details
          class="inputs-actions__search"
          :disabled="itemsNotReadyForDisplay"
          :label="$t('actions.search')"
          single-line
          append-icon="mdi-magnify"
          @input="debouncedFilterChange"
        />
      </v-col>

      <div class="d-flex align-center mx-1">
        <norm-weight-toggle
          class="norm-weight-toggle"
          @change="updateColumnDefsPreservingSort(storegroupKeyNameMap)"
        />
      </div>

      <v-col v-if="!isUnitManagerView" cols="2" class="pr-2">
        <scenario-filter
          :hide-pricing-group-selection="true"
          :dense="false"
          :solo="false"
          :hide-label="true"
          @selectScenario="selectScenario"
        />
      </v-col>

      <v-divider v-if="!isUnitManagerView" vertical class="controls-row__divider" />

      <v-col class="d-flex align-center engine-controls-col">
        <engine-controls
          v-if="!isUnitManagerView"
          :level-entry-key="levelEntryKey"
          :pricing-group-name="pricingGroupName"
          :show-tool-store-group="isGroupTSGsEnabled"
          :items="filteredItems"
          @bulk-action-selected="handleBulkAction"
        />
      </v-col>

      <v-col cols="auto" class="d-flex align-center pr-2 filter-col">
        <div class="d-flex align-center filter-buttons-container">
          <span class="filter-by-text mr-2">Filter by:</span>
          <v-btn
            class="columns-toggle grid-btn pa-0 mr-2"
            secondary
            small
            depressed
            @click="toggleSideBar('columns')"
          >
            <v-icon class="mr-1">mdi-view-column-outline</v-icon>
            {{ $t('inputs.columns') }}
          </v-btn>
          <v-btn
            class="layouts-toggle grid-btn pa-0"
            secondary
            small
            depressed
            @click="toggleSideBar('layouts')"
          >
            {{ $t('grid.layouts.button') }}
            <v-icon class="material-icons-outlined ml-1 sidebar-toggle">
              mdi-view-dashboard-outline
            </v-icon>
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-alert v-if="noLayoutsExist" type="info" text class="mx-4 mt-2">
      {{ $t('grid.layouts.sidebar.noLayouts') }}
    </v-alert>

    <div class="grid-view-ag-grid-content">
      <!-- need to set rowData to null while loading to display overlay for ag-grid <= 31. otherwise shows no rows -->
      <ag-grid-vue
        style="width: 100%; height: 100%;"
        class="ag-theme-custom ag-product-list-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :loading="itemsNotReadyForDisplay"
        :default-col-def="defaultColDef"
        :row-data="itemsNotReadyForDisplay ? null : rowData"
        :side-bar="sideBar"
        :fill-operation="fillOperation"
        @grid-ready="onGridReady"
        @column-visible="onColumnVisible"
      />
    </div>

    <v-layout row justify-center>
      <v-dialog
        v-model="isProductDialogOpen"
        @click:outside="closeDialog"
        @input="dialogVisible => !dialogVisible && closeDialog()"
      >
        <v-card v-if="selectedProduct">
          <v-card-title v-if="expandedProduct" class="headline">
            {{ expandedProduct.article.productKeyDisplay }} -
            {{ expandedProduct.article.productDescription }}
          </v-card-title>
          <v-icon class="close-button" color="grey" @click="closeDialog">
            close
          </v-icon>
          <v-card-text class="product-dialog-content">
            <expanded-product-view
              :key="`product-${selectedProduct.productKey}-${selectedProduct.toolStoreGroupKey}`"
              :product="selectedProduct"
              :is-unit-manager-view="isUnitManagerView"
              @toggleFilteringByArchitectureGroup="onToggleFilteringByArchitectureGroup"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn class="primary close-btn" @click="closeDialog()">
              {{ $t('actions.close') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-layout>

    <confirm-dialog
      :is-open="isBulkConfirmDialogOpen"
      :ok-btn-text="$t('actions.confirm')"
      :cancel-btn-text="$t('actions.cancel')"
      @onOk="executeBulkOverride"
      @onCancel="cancelBulkOverride"
    >
      <template v-slot:body>
        <div>
          <p>{{ bulkConfirmData.actionLabel }}</p>
          <p v-if="bulkConfirmData.isRemove && bulkConfirmData.blocked > 0" class="warning-text">
            {{
              $t('gridView.bulkActions.confirmRemoveWithSkipped', {
                skipped: bulkConfirmData.blocked,
                eligible: bulkConfirmData.eligible,
              })
            }}
          </p>
          <p
            v-else-if="!bulkConfirmData.isRemove && bulkConfirmData.blocked > 0"
            class="warning-text"
          >
            {{
              $t('gridView.bulkActions.confirmWithBlocked', {
                blocked: bulkConfirmData.blocked,
                eligible: bulkConfirmData.eligible,
              })
            }}
          </p>
          <p v-else>
            {{ $t('gridView.bulkActions.confirmMessage', { count: bulkConfirmData.total }) }}
          </p>
        </div>
      </template>
    </confirm-dialog>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue';
import {
  flatMap,
  groupBy,
  debounce,
  get,
  isEmpty,
  isUndefined,
  isNumber,
  isFunction,
  set,
  isNil,
  omit,
  difference,
  union,
  round,
  first,
} from 'lodash';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { categoryLevel, pricingGroupLevel, architectureGroupLevel } from '@enums/hierarchy';
import assortmentRoles from '@enums/assortment-roles';
import {
  useZones,
  useForecastVolume,
  displayArchitectureSubGroupSplittingAttributes,
  useStoreGroupChains,
  useFirstFuturePrice,
  maxAdditionalGridViewAttributes,
  useAbsoluteCompetitorIndex,
  allowStoreGroupPriceLocking,
  displayDistributionByAssortmentRole,
  showAdditionalCostTypes,
  displayClientProductKey,
  displayCompetitorHistoricalPrices,
  selectCompetitorsOnExport,
  marginComparisonType,
  enablePriceOptimisation,
  allowBulkPriceOverrides,
  enableReferenceDate,
  enableSimilarityGroups,
  displayCompetitorPromotion,
  showUnsimulatedIndex,
  cancelScenarioPriceWithDoubleClick,
  enableGridViewFillHandle,
} from '@enums/feature-flags';
import gridTypes from '@enums/grid-types';
import numberFormats from '@enums/number-formats';
import { ascending } from '@enums/sort-direction';
import agGridCellRendererComponentWrapper from '../../../components/ag-grid-cell-renderers/ag-grid-cell-renderer-component-wrapper.vue';
import featureFlagsMixin from '../../../mixins/featureFlags';
import getPriceFieldMixin from '../../../mixins/getPriceField';
import tooltipFormatterMixin from '../../../mixins/tooltipFormatter';
import priceOverrideMixin from '../../../mixins/priceOverride';
import bulkPriceOverrideMixin from '../../../mixins/bulkPriceOverride';
import sideBarMixin from '../../../mixins/sideBar';
import agGridUtils from '../../../utils/ag-grid-utils';
import {
  hasToolStoreGroupOrChainFilters,
  detectToolStoreGroupOrChainFilterChanges,
} from '../../../utils/filter-util';
import { getTsgKeyFromGroupId, isToolStoreGroup } from '../../../utils/tsg-utils';
import moment from 'moment';
import {
  yearMonthDayFormat,
  monthDayYearFullSlashesFormat,
  hoursMinutesDayMonthYearSeparatedFormat,
} from '@enums/date-formats';
import gridViewPriceOverrideCellRenderer from '../../../components/ag-grid-cell-renderers/grid-view-price-override-cell-renderer.vue';
import datePickerEditor from '../../../components/date-picker-editor.vue';
import CustomNumberCellEditor from '../../inputs/engine-inputs/localisedNumberCellEditor';
import layouts from '../../../components/layouts/layouts.vue';
import toggleHeader from '../../../components/toggle-header.vue';
import tsgEngineControlsHeader from '../../../components/tsg-engine-controls-header.vue';
import SelectCompetitorsDownloadDialog from './select-competitors-download-dialog.vue';
import FeatureToggle from '../../../components/feature-toggle.vue';
import { exportGridWithCustomColumns } from '../../../utils/grid-view-ag-grid-export-utils';
import TsgScopedCheckboxCellRenderer from '../../../components/ag-grid-cell-renderers/tsg-scoped-checkbox-cell-renderer.vue';
import TsgScopedHeaderCheckbox from '../../../components/ag-grid-cell-renderers/tsg-scoped-header-checkbox.vue';
import ConfirmDialog from '../../../components/confirm-dialog.vue';
import PriceValidationBadge from '../../../components/ag-grid-cell-renderers/price-validation-badge.vue';

export default {
  name: 'GridViewAgGrid',

  components: {
    AgGridVue,
    // eslint-disable-next-line vue/no-unused-components
    agGridCellRendererComponentWrapper,
    // eslint-disable-next-line vue/no-unused-components
    gridViewPriceOverrideCellRenderer,
    // eslint-disable-next-line vue/no-unused-components
    datePickerEditor,
    // eslint-disable-next-line vue/no-unused-components
    layouts,
    // eslint-disable-next-line vue/no-unused-components
    toggleHeader,
    // eslint-disable-next-line vue/no-unused-components
    tsgEngineControlsHeader,
    SelectCompetitorsDownloadDialog,
    FeatureToggle,
    ConfirmDialog,
    // eslint-disable-next-line vue/no-unused-components
    TsgScopedCheckboxCellRenderer,
    // eslint-disable-next-line vue/no-unused-components
    TsgScopedHeaderCheckbox,
    // eslint-disable-next-line vue/no-unused-components
    PriceValidationBadge,
  },

  mixins: [
    featureFlagsMixin,
    getPriceFieldMixin,
    tooltipFormatterMixin,
    priceOverrideMixin,
    bulkPriceOverrideMixin,
    sideBarMixin,
  ],

  props: {
    isUnitManagerView: {
      type: Boolean,
      required: true,
    },
    levelEntryKey: {
      type: String,
      required: true,
    },
    tensionChartOptions: {
      type: Object,
      required: true,
    },
    pricingGroupName: {
      type: String,
      default: '',
    },
  },

  events: {
    onNewCandidateScenarioElected() {
      this.fetchLastTriggered();
    },
  },

  data() {
    return {
      gridApi: null,
      isProductDialogOpen: false,
      selectedProduct: null,
      columnDefs: null,
      useZones,
      selectCompetitorsOnExport,
      isSelectCompetitorsDialogOpen: false,
      noLayoutsExist: false,
      previousValidationProductKeys: new Set(),
      failedValidationTsgKeysByProductKey: new Map(),
      gridOptions: {
        accentedSort: true,
        alwaysShowHorizontalScroll: true,
        enableFillHandle: false,
        enableRangeSelection: true,
        fillHandleDirection: 'y',
        headerHeight: 30,
        rowHeight: 28,
        singleClickEdit: true,
        stopEditingWhenCellsLoseFocus: true,
        suppressMenuHide: true,
        suppressRowDrag: true,
        suppressScrollOnNewData: true,
        tooltipShowDelay: 200,
        getContextMenuItems: this.getContextMenuItems,
        components: {
          CustomNumberCellEditor,
          layouts,
        },
        getRowClass: this.getRowClass,
        overlayNoRowsTemplate: `<span class="ag-overlay-loading-center">${this.$t(
          'grid.applyFilter'
        )}</span>`,
        overlayLoadingTemplate: `<span class="ag-overlay-loading-center">${this.$t(
          'grid.loading'
        )}</span>`,
        context: {
          componentParent: this,
          gridType: gridTypes.GRIDVIEW,
        },
        excelStyles: [
          {
            id: 'stringType',
            dataType: 'String',
          },
          {
            id: 'dateType',
            dataType: 'DateTime',
          },
        ],
        suppressRowClickSelection: true,
        // Preserve row positions across all columns after a price edit, until the user
        // deliberately re-applies sort. stableSortRowOrder is set by
        // handleScenarioPriceValueSetter and cleared by the sortChanged listener.
        postSortRows: params => {
          if (!this.stableSortRowOrder) return;
          const orderMap = new Map();
          this.stableSortRowOrder.forEach((id, index) => orderMap.set(id, index));
          params.nodes.sort((a, b) => {
            const indexA = orderMap.has(a.id) ? orderMap.get(a.id) : Infinity;
            const indexB = orderMap.has(b.id) ? orderMap.get(b.id) : Infinity;
            return indexA - indexB;
          });
        },
      },
      debouncedUpdateBoundaries: null,
      debouncedReconcileSelection: null,
      searchString: '',
      updatingSyncedColumns: false, // Flag to prevent infinite loop when hiding/showing columns together (in sync)
      highlightedProductKey: null, // Tracks the row opened in product reveal for persistent highlight
      stableSortRowOrder: null, // Captured row order to freeze positions during stable sort mode
    };
  },
  computed: {
    ...mapState('clientConfig', ['toggleLogic', 'alertTypes', 'i18nconfig', 'priceValidations']),
    ...mapState('filters', [
      'pricingGroup',
      'selectedPriceType',
      'retailAttributesFilter',
      'scenario',
    ]),
    ...mapState('gridView', [
      'selectedCompetitors',
      'showRegularImpact',
      'loadingItems',
      'currentColumnState',
      'calculatingScenarioResults',
      'recalculatingAll',
      'calculatingPgResults',
      'calculatingSpecificPgResults',
      'calculatingScenarioElasticityResults',
      'expandedProduct',
      'selectedHistoricalPeriod',
    ]),
    ...mapState('storeGroupRelationships', ['storeGroupChains', 'storegroupKeyNameMap']),
    ...mapState('workpackages', ['selectedWorkpackage']),
    ...mapState('scenarioMetadata', ['scenarioMetadata']),
    ...mapGetters('scenarioMetadata', [
      'getCandidateScenario',
      'lockedPricesByTsgKey',
      'lockedPricesDateByTsgKey',
    ]),
    ...mapGetters('gridView', [
      'readFromDatabricks',
      'itemOnPromotion',
      'itemHasUpcomingPromotion',
      'visibleCompetitors',
      'filteredItems',
    ]),
    ...mapGetters('competitorMetadata', ['nonStoreGroupCompetitors']),
    ...mapGetters('context', ['isWholesaleManager', 'isWholesaleAnalyst', 'isAdminLoggedIn']),
    ...mapGetters('attributes', [
      'getGridDefaultAttributes',
      'getGridNonDefaultAttributes',
      'getGridDisplayedAttributes',
    ]),
    ...mapGetters('gridLayouts', ['getActiveLayout']),
    ...mapGetters('storeGroupRelationships', ['chainTsgKeyByTsgKey']),
    ...mapGetters('priceValidations', ['validationsByProductKey']),
    activeLayout() {
      return this.getActiveLayout(gridTypes.GRIDVIEW);
    },
    isGroupTSGsEnabled() {
      return this.isFeatureFlagEnabled(useZones) && this.isFeatureFlagEnabled(useStoreGroupChains);
    },
    isPriceValidationsEnabled() {
      return !!(this.priceValidations && this.priceValidations.enabled);
    },
    enableReferenceDate() {
      return this.isFeatureFlagEnabled(enableReferenceDate);
    },

    itemsNotReadyForDisplay() {
      return this.loadingItems || !this.filteredItems.length;
    },

    defaultColDef() {
      return {
        autoHeaderHeight: true,
        editable: false,
        filter: 'agMultiColumnFilter',
        headerClass: 'ag-product-list-grid__header',
        // open product dialog on cell click
        onCellClicked: params => {
          // ignore alerts and PO columns
          if (
            params.colDef.headerName === this.$t('pricing.alerts') ||
            params.colDef.headerName === this.$t('pricing.scenarioPrice') ||
            params.colDef.headerName === this.$t('pricing.productKey') ||
            params.colDef.headerName === this.$t('pricing.overrideDateRange') ||
            params.colDef.colId === 'priceValidations' ||
            (params.colDef.colId && params.colDef.colId.endsWith('selection')) // Ignore clicks on custom selection column
          ) {
            return;
          }

          // get parent toolStoreGroup groupId
          const groupId = get(params, 'column.parent.parent.groupId');

          if (this.isGroupTSGsEnabled && isToolStoreGroup(groupId)) {
            const tsgKey = getTsgKeyFromGroupId(groupId);
            const productTsgData = get(params.data, ['toolStoreGroups', tsgKey], {});

            if (!productTsgData || isEmpty(productTsgData)) {
              return;
            }

            this.selectedProduct = {
              ...productTsgData,
              productKeyDisplay: params.data.productKeyDisplay,
              productDescription: params.data.productDescription,
              subGroupValues: params.data.subGroupValues,
              availableProductTSGsKeys: Object.keys(get(params.data, 'toolStoreGroups', {})),
            };
          } else {
            // when grouping mode is enabled and clicked cell is a non TSG related, open default TSG
            this.selectedProduct =
              this.isGroupTSGsEnabled && !isEmpty(params.data.toolStoreGroups)
                ? {
                    ...params.data,
                    toolStoreGroupKey: null,
                    availableProductTSGsKeys: Object.keys(get(params.data, 'toolStoreGroups', {})),
                  }
                : params.data;
          }

          if (!this.selectedProduct) {
            return;
          }

          const prevKey = this.highlightedProductKey;
          this.highlightedProductKey = params.data.productKey;
          if (this.gridApi) {
            // Redraw only the previously highlighted row and the newly highlighted row,
            // rather than the entire grid, to avoid unnecessary re-renders on large datasets.
            const nodesToRedraw = [params.node];
            if (prevKey && prevKey !== params.data.productKey) {
              this.gridApi.forEachNode(node => {
                if (get(node, 'data.productKey') === prevKey) {
                  nodesToRedraw.push(node);
                }
              });
            }
            this.gridApi.redrawRows({ rowNodes: nodesToRedraw });
          }
          this.isProductDialogOpen = true;
        },
        menuTabs: ['filterMenuTab', 'generalMenuTab'],
        resizable: true,
        width: 70,
        wrapHeaderText: true,
      };
    },

    rowData() {
      if (!this.isGroupTSGsEnabled) {
        return this.filteredItems;
      }

      const groupedItems = groupBy(this.filteredItems, 'productKey');
      return Object.entries(groupedItems).map(([productKey, rows]) => ({
        productKey,
        productKeyDisplay: rows[0].productKeyDisplay,
        clientProductKey: rows[0].clientProductKey,
        productDescription: rows[0].productDescription,
        productSizeType: rows[0].productSizeType,
        hierarchy: rows[0].hierarchy,
        similarityGroupDescription: rows[0].similarityGroupDescription,
        cannGroupName: rows[0].cannGroupName,
        scenarioKey: rows[0].scenarioKey,
        toolStoreGroupKey: rows[0].toolStoreGroupKey,
        toolStoreGroupDescription: rows[0].toolStoreGroupDescription,
        subGroupValues: rows[0].subGroupValues,
        attributes: rows[0].attributes,
        toolStoreGroups: rows.reduce((acc, r) => {
          acc[r.toolStoreGroupKey] = omit(r, [
            'clientProductKey',
            'productDescription',
            'productSizeType',
            'subGroupValues',
            'attributes',
            'toolStoreGroups',
            'similarityGroupDescription',
            'cannGroupName',
          ]);
          return acc;
        }, {}),
      }));
    },

    tensionLabel() {
      return this.isFeatureFlagEnabled(useZones) ? 'pricing.tensionUseZones' : 'pricing.tension';
    },

    livePricePath() {
      return this.readFromDatabricks
        ? this.getPriceFieldObjectPath('livePrice')
        : this.getPriceField('livePrice');
    },

    liveCostPath() {
      return this.readFromDatabricks
        ? this.getPriceFieldObjectPath('liveCost')
        : this.getPriceField('liveCost');
    },

    productInfoColumns() {
      const columns = [
        {
          headerName: this.$t('pricing.productKey'),
          field: 'productKeyDisplay',
          colId: 'productKeyDisplay',
          width: 73,
          pinned: 'left',
          cellDataType: 'text',
          cellClass: 'stringType',
        },
        {
          headerName: this.$t('pricing.clientProductKey'),
          field: 'clientProductKey',
          colId: 'clientProductKey',
          width: 73,
          pinned: 'left',
          cellDataType: 'text',
          cellClass: 'stringType',
          ...this.getColumnVisibilityState(displayClientProductKey),
          suppressColumnsToolPanel: !this.isFeatureFlagEnabled(displayClientProductKey),
        },
        {
          headerName: this.$t('pricing.productDescription'),
          field: 'productDescription',
          colId: 'productDescription',
          sort:
            this.isUnitManagerView || this.isFeatureFlagEnabled(useForecastVolume)
              ? undefined
              : 'asc',
          sortIndex:
            this.isUnitManagerView || this.isFeatureFlagEnabled(useForecastVolume) ? undefined : 0,
          width: 200,
          cellClass: ['font-weight-bold'],
          pinned: 'left',
        },
        {
          headerName: this.$t('pricing.productSizeType'),
          field: 'productSizeType',
          colId: 'productSizeType',
          pinned: 'left',
        },
      ];

      // add Alerts column when not in group TSGs mode
      if (!this.isGroupTSGsEnabled) {
        columns.splice(
          0,
          0,
          this.getAlertsColumn({ prefix: '', tooltipPosition: 'top-right', pinned: 'left' })
        );
      }

      // add TSG column when use zones is enabled and group TSGs is disabled
      if (this.isFeatureFlagEnabled(useZones) && !this.isFeatureFlagEnabled(useStoreGroupChains)) {
        columns.splice(2, 0, {
          headerName: this.$t('pricing.toolStoreGroup'),
          field: 'toolStoreGroupDescription',
          colId: 'toolStoreGroupDescription',
          width: 100,
        });
      }

      return columns;
    },
    productHierarchyColumns() {
      const displayArchitectureSubGroupSplittingAttributesEnabled = this.isFeatureFlagEnabled(
        displayArchitectureSubGroupSplittingAttributes
      );

      const columns = [
        {
          headerName: this.$t('pricing.category'),
          field: `hierarchy.${categoryLevel}.levelEntryDescription`,
          colId: `hierarchy.${categoryLevel}.levelEntryDescription`,
          width: 100,
          hide: true, // hide category column by default
        },
        {
          headerName: this.$t('pricing.pricingGroup'),
          field: `hierarchy.${pricingGroupLevel}.levelEntryDescription`,
          colId: `hierarchy.${pricingGroupLevel}.levelEntryDescription`,
          width: 100,
        },
        {
          headerName: this.$t('pricing.architectureGroup'),
          field: `hierarchy.${architectureGroupLevel}.levelEntryDescription`,
          colId: `hierarchy.${architectureGroupLevel}.levelEntryDescription`,
          width: 100,
        },
        ...(displayArchitectureSubGroupSplittingAttributesEnabled
          ? [
              {
                headerName: this.$t('pricing.architectureSubGroupSplittingAttributes'),
                field: 'subGroupValues',
                colId: 'subGroupValues',
                width: 100,
                valueFormatter: params =>
                  isUndefined(params.value)
                    ? ''
                    : params.value
                        .map(value =>
                          isNumber(value) ? this.formatNumber({ number: value }) : value
                        )
                        .join('-'),
              },
            ]
          : []),
        ...(this.isFeatureFlagEnabled(enableSimilarityGroups)
          ? [
              {
                headerName: this.$t('pricing.similarityGroup'),
                field: 'similarityGroupDescription',
                colId: 'similarityGroupDescription',
                width: 100,
              },
              {
                headerName: this.$t('pricing.cannGroup'),
                field: 'cannGroupName',
                colId: 'cannGroupName',
                width: 100,
              },
            ]
          : []),
      ];

      return columns;
    },

    attributesColumns() {
      const defaultAttributes = this.getGridDefaultAttributes.map(
        ({ attributeKey, attributeDescription }) => ({
          headerName: this.$t(`pricing.attributes.${attributeDescription}`),
          field: `attributes.${attributeKey}`,
          colId: `attributes.${attributeKey}`,
          ...this.getAttributeColumnVisibilityState(attributeKey, {
            isDefault: true,
            activeLayout: !!this.activeLayout,
          }),
        })
      );
      const nonDefaultAttributes = this.getGridNonDefaultAttributes.map(
        ({ attributeKey, displayDescription }) => ({
          headerName: displayDescription,
          field: `attributes.${attributeKey}`,
          colId: `attributes.${attributeKey}`,
          ...this.getAttributeColumnVisibilityState(attributeKey, {
            activeLayout: !!this.activeLayout,
          }),
        })
      );
      return [...defaultAttributes, ...nonDefaultAttributes];
    },

    isEditable() {
      return this.isAdminLoggedIn || (!this.isWholesaleManager && !this.isWholesaleAnalyst);
    },

    isPriceOverrideEditable() {
      return this.isEditable;
    },

    sideBar() {
      return {
        toolPanels: [
          this.columnToolPanel,
          {
            id: 'layouts',
            labelDefault: '',
            labelKey: 'layouts',
            iconKey: 'dashboard',
            toolPanel: 'layouts',
            toolPanelParams: {
              suppressPivotMode: true,
              suppressPivots: true,
              suppressRowGroups: true,
              suppressValues: true,
              onLayoutApplied: this.onLayoutApplied,
            },
            width: 385,
          },
        ],
        hiddenByDefault: true,
      };
    },
  },

  watch: {
    loadingItems(newVal, oldVal) {
      if (this.gridApi) {
        this.gridApi.showLoadingOverlay();
      }
      if (this.isPriceValidationsEnabled) {
        // Reset tracked validation keys when a new load starts (e.g. workpackage/scenario change)
        if (newVal === true) {
          this.previousValidationProductKeys = new Set();
        }
        // When items finish loading, fetch validation data
        if (oldVal === true && newVal === false && this.selectedWorkpackage) {
          this.fetchValidations({
            workpackageId: this.selectedWorkpackage._id,
            scenarioKey: this.scenario,
          });
        }
      }
    },
    validationsByProductKey(newMap) {
      if (!this.isPriceValidationsEnabled || !this.gridApi) return;

      this.failedValidationTsgKeysByProductKey = this.buildFailedValidationTsgKeysMap(newMap);

      const newKeys = new Set(newMap.keys());
      const prevKeys = this.previousValidationProductKeys;

      // Refresh all rows that currently have validations or previously had them,
      // so both the badge column and per-TSG scenario price cell highlights update.
      const affectedProductKeys = new Set([...newKeys, ...prevKeys]);
      const rowNodesToRefresh = [];
      this.gridApi.forEachNode(node => {
        if (node.data && affectedProductKeys.has(Number(node.data.productKey))) {
          rowNodesToRefresh.push(node);
        }
      });

      if (rowNodesToRefresh.length > 0) {
        this.gridApi.refreshCells({ rowNodes: rowNodesToRefresh, force: true });
      }

      // Refresh the set filter values so the dropdown reflects the current
      // validation state without waiting for a full data reload (fixes #3/#4).
      const filterInstance = this.gridApi.getFilterInstance('priceValidations');
      if (filterInstance) {
        filterInstance.refreshFilterValues();
      }

      this.previousValidationProductKeys = newKeys;
    },
    showRegularImpact() {
      this.updateColumnDefsPreservingSort(this.storegroupKeyNameMap);
    },
    currentColumnState: {
      handler(newColumnState) {
        // Apply column state changes to the ag-Grid when the store updates
        if (this.gridApi && newColumnState && Array.isArray(newColumnState)) {
          this.applyGridColumnState(newColumnState);
        }
      },
    },
    retailAttributesFilter: {
      async handler(newFilter, oldFilter) {
        // Check if tool store group or chain filters have changed
        const { toolStoreGroupChanged, chainChanged } = detectToolStoreGroupOrChainFilterChanges(
          newFilter,
          oldFilter
        );

        // Update if either tool store group or chain filter values have changed
        if (toolStoreGroupChanged || chainChanged) {
          const baseColumnState = this.activeLayout
            ? this.activeLayout.columnState
            : this.currentColumnState || this.gridApi.getColumnState();

          if (baseColumnState) {
            this.applyGridColumnState(baseColumnState);
          }

          const updatedColumnState = await this.updateColumnStateForToolStoreGroupFilter({
            columnState: baseColumnState,
          });

          // If column state was updated, trigger refetch if needed
          if (updatedColumnState) {
            this.updateColumnStateWithOptionalRefetch({
              levelEntryKey: this.levelEntryKey,
              isUnitManagerView: this.isUnitManagerView,
              columnState: updatedColumnState,
            });
          }

          // Reconcile bulk selection after filter changes that affect visible data
          if (
            this.isFeatureFlagEnabled(allowBulkPriceOverrides) &&
            this.debouncedReconcileSelection
          ) {
            this.debouncedReconcileSelection();
          }
        }
      },
    },
    calculatingScenarioResults(newValue, oldValue) {
      if (oldValue === true && newValue === false) {
        // Only re-fetch last triggered info when calculation finishes
        this.fetchLastTriggered();
      }
    },
    recalculatingAll(newValue, oldValue) {
      if (oldValue === true && newValue === false) {
        // Only re-fetch last triggered info when calculation finishes
        this.fetchLastTriggered();
      }
    },
    calculatingPgResults(newValue, oldValue) {
      if (oldValue === true && newValue === false) {
        // Only re-fetch last triggered info when calculation finishes
        this.fetchLastTriggered();
      }
    },
    calculatingSpecificPgResults(newValue, oldValue) {
      if (oldValue === true && newValue === false) {
        // Only re-fetch last triggered info when calculation finishes
        this.fetchLastTriggered();
      }
    },
    calculatingScenarioElasticityResults(newValue, oldValue) {
      if (oldValue === true && newValue === false) {
        // Only re-fetch last triggered info when calculation finishes
        this.fetchLastTriggered();
      }
    },
  },

  async created() {
    if (this.isFeatureFlagEnabled(enableGridViewFillHandle)) {
      this.gridOptions.enableFillHandle = true;
    }

    const defaultAttributes = this.getGridDefaultAttributes.map(({ attributeKey }) => attributeKey);
    this.setGridDisplayedAttributes(new Set(defaultAttributes));
    this.additionalAttributes = new Set(defaultAttributes);
    // by default load grid with all store group columns displayed
    this.columnDefs = this.getColumnDefs(this.storegroupKeyNameMap);

    // Create the debounced function once during component initialization
    this.debouncedUpdateBoundaries = debounce(() => {
      this.updateColumnGroupBoundaryClasses();
    }, 200);

    // Create debounced reconcile selection function (only if bulk overrides enabled)
    if (this.isFeatureFlagEnabled(allowBulkPriceOverrides)) {
      this.debouncedReconcileSelection = debounce(() => {
        this.reconcileSelection();
      }, 300);
    }

    if (!this.isUnitManagerView) {
      // set selected scenarioKey filter to fetch products
      await this.fetchScenarioMetadata({
        params: {
          where: { hierarchyId: this.pricingGroup },
          sortBy: 'scenarioDescription',
          sortDirection: ascending,
        },
      });
      const candidateScenario = this.getCandidateScenario;
      this.setSelectedFilter({
        filterName: 'scenario',
        filterValue: candidateScenario.scenarioKey,
      });
    }

    if (this.isUnitManagerView) {
      await this.initializeAndFetchData();
    }

    this.fetchLastTriggered();
  },

  beforeDestroy() {
    // Clear bulk selection to prevent leaking selection across pricing groups
    if (this.isFeatureFlagEnabled(allowBulkPriceOverrides) && this.clearAllSelections) {
      this.clearAllSelections();
    }

    // remove currentColumnState from store
    this.setCurrentColumnState(null);
    // Cancel any pending debounced calls
    if (this.debouncedUpdateBoundaries) {
      this.debouncedUpdateBoundaries.cancel();
    }
    if (this.debouncedReconcileSelection) {
      this.debouncedReconcileSelection.cancel();
    }

    // Remove event listeners using the same debounced function reference
    if (this.gridApi) {
      ['firstDataRendered', 'displayedColumnsChanged'].forEach(event =>
        this.gridApi.removeEventListener(event, this.debouncedUpdateBoundaries)
      );

      if (this.isFeatureFlagEnabled(allowBulkPriceOverrides)) {
        ['filterChanged', 'modelUpdated'].forEach(event =>
          this.gridApi.removeEventListener(event, this.debouncedReconcileSelection)
        );
      }
    }
  },

  methods: {
    ...mapMutations('attributes', ['setGridDisplayedAttributes']),
    ...mapMutations('notifications', ['addNotification']),
    ...mapMutations('gridView', ['setVisibleCompetitors', 'setBulkUnsavedKpiResults']),
    ...mapActions('gridView', [
      'fetchItems',
      'updateOverridePrice',
      'setCurrentColumnState',
      'updateColumnStateWithOptionalRefetch',
      'updateColumnStateForCompetitorChange',
      'updateColumnStateForToolStoreGroupFilter',
      'fetchLastTriggeredInfo',
    ]),
    ...mapActions('gridLayouts', ['initLayoutsAndSetActive']),
    ...mapActions('filters', ['setSelectedFilter']),
    ...mapActions('scenarioMetadata', ['fetchScenarioMetadata', 'updateLockedPrices']),
    ...mapActions('priceValidations', ['fetchValidations']),

    getRowClass(params) {
      if (params.data) {
        const classes = [];

        // Check for current promotions only (not upcoming) at the top level
        let hasCurrentPromotion = this.itemOnPromotion(params.data);

        // If tool store groups are enabled, also check current promotion status within each TSG
        if (this.isGroupTSGsEnabled && params.data.toolStoreGroups && !hasCurrentPromotion) {
          Object.values(params.data.toolStoreGroups).some(tsgData => {
            if (!hasCurrentPromotion) {
              hasCurrentPromotion = this.itemOnPromotion(tsgData);
            }
            // Return true to break early if current promotion is found
            return hasCurrentPromotion;
          });
        }

        // Only highlight the row if there's a current promotion (not upcoming)
        if (hasCurrentPromotion) {
          classes.push('promotion-row');
        }

        // Persist highlight on the row that was last opened in product reveal
        if (params.data.productKey === this.highlightedProductKey) {
          classes.push('highlighted-row');
        }

        return classes.join(' ');
      }

      // Always return something (empty string for no classes)
      return '';
    },

    buildFailedValidationTsgKeysMap(validationsMap) {
      const result = new Map();

      validationsMap.forEach((productValidations, productKey) => {
        const failedTsgKeys = new Set();
        productValidations.forEach(validation => {
          if (validation.severity !== 'error') {
            return;
          }
          (validation.failures || []).forEach(failure => {
            if (failure.leftStoreGroupKey) {
              failedTsgKeys.add(String(failure.leftStoreGroupKey));
            }
            if (failure.rightStoreGroupKey) {
              failedTsgKeys.add(String(failure.rightStoreGroupKey));
            }
          });
        });

        if (failedTsgKeys.size > 0) {
          result.set(Number(productKey), failedTsgKeys);
        }
      });

      return result;
    },

    isValidationFailureCell(params) {
      if (!this.isPriceValidationsEnabled || !params || !params.data) {
        return false;
      }

      const productKey = Number(params.data.productKey);
      const failedTsgKeys = this.failedValidationTsgKeysByProductKey.get(productKey);
      if (!failedTsgKeys || failedTsgKeys.size === 0) {
        return false;
      }

      const tsgKey = get(params, 'colDef.context.tsgKey', params.data.toolStoreGroupKey);
      if (isNil(tsgKey)) {
        return false;
      }

      return failedTsgKeys.has(String(tsgKey));
    },

    /**
     * Helper to fetch last triggered info with required parameters
     */
    fetchLastTriggered() {
      this.fetchLastTriggeredInfo({
        workpackageId: get(this.selectedWorkpackage, '_id'),
        scenarioKey: this.scenario || get(first(this.filteredItems), 'scenarioKey'),
        levelEntryKey: this.levelEntryKey,
        storegroupKeyNameMap: this.storegroupKeyNameMap,
        isGroupTSGsEnabled: this.isGroupTSGsEnabled,
      });
    },

    /**
     * Regenerates column definitions while preserving any user-applied sort state.
     * Called when toggling Normweight/UOM/Regular or when showRegularImpact changes,
     * so that rebuilding column defs does not reset a user-applied sort.
     */
    updateColumnDefsPreservingSort(storeGroupMap) {
      // Capture current sort state before regenerating column defs
      const sortStateToRestore = this.gridApi
        ? this.gridApi.getColumnState().filter(col => !isNil(col.sort))
        : null;

      this.columnDefs = this.getColumnDefs(storeGroupMap);

      if (sortStateToRestore && sortStateToRestore.length && this.gridApi) {
        this.$nextTick(() => {
          // defaultState clears sort on every column that is NOT in sortStateToRestore,
          // overriding any sort values baked into freshly-generated column defs
          // (e.g. productDescription sort:'asc'). Without this, regenerating column defs
          // causes ag-grid to re-apply those defaults alongside the user's sort.
          this.gridApi.applyColumnState({
            state: sortStateToRestore,
            defaultState: { sort: null, sortIndex: null },
            applyOrder: false,
          });
        });
      }
    },

    /**
     * Helper method to initialize layouts and fetch data if needed
     * Handles both cases: when layouts are enabled (data fetched via onLayoutApplied)
     * and when layouts are disabled (manual data fetching)
     */
    async initializeAndFetchData() {
      // Always try to initialise layouts first
      if (!this.activeLayout) {
        const { layouts: fetchedLayouts, activeLayout } = await this.initLayoutsAndSetActive({
          gridType: gridTypes.GRIDVIEW,
        });

        // Auto-activate the first available layout when the user has no saved preference
        if (!activeLayout && fetchedLayouts.length > 0) {
          await this.$store.dispatch('gridLayouts/setActiveLayoutAndSave', {
            layout: fetchedLayouts[0],
            gridType: gridTypes.GRIDVIEW,
          });
        } else if (!activeLayout && fetchedLayouts.length === 0) {
          // No layouts exist at all — inform the user to save their first layout
          this.noLayoutsExist = true;
        }
      }

      // Two possible paths after layout initialisation:
      // 1. An active layout was set → data will be fetched via onLayoutApplied
      // 2. No layouts exist → fetch data manually with a full payload
      if (!this.activeLayout) {
        this.fetchItems({
          levelEntryKey: this.levelEntryKey,
          isUnitManagerView: this.isUnitManagerView,
        });
      }
    },

    getColumnDefs(storeGroupMap) {
      const columns = this.isGroupTSGsEnabled
        ? this.storeGroupChains.map(chain => ({
            headerName: chain.description,
            groupId: chain._id,
            marryChildren: true,
            children: Object.entries(storeGroupMap)
              .filter(([tsgKey]) => chain.toolStoreGroupKeys.includes(tsgKey))
              .sort(([, descA], [, descB]) => descA.localeCompare(descB))
              .map(([tsgKey, tsgDescription]) => ({
                headerName: tsgDescription,
                groupId: `toolStoreGroup::${tsgKey}`,
                marryChildren: true,
                headerGroupComponent: 'tsgEngineControlsHeader',
                headerGroupComponentParams: {
                  headerName: tsgDescription,
                  toolStoreGroupKey: tsgKey,
                  isUnitManagerView: this.isUnitManagerView,
                  levelEntryKey: this.levelEntryKey,
                  pricingGroupName: this.pricingGroupName,
                },
                children: this.getColumns(tsgKey),
              })),
          }))
        : this.getColumns();

      const validationColumnGroup = this.isPriceValidationsEnabled
        ? [
            {
              headerName: this.$t('pricing.mainHeaders.validations'),
              groupId: 'validations',
              marryChildren: true,
              children: [this.getValidationColumn()],
            },
          ]
        : [];

      return [
        ...validationColumnGroup,
        {
          headerName: this.$t('pricing.mainHeaders.productInfo'),
          groupId: 'productInfo',
          marryChildren: true,
          children: this.productInfoColumns,
        },
        {
          headerName: this.$t('pricing.mainHeaders.hierarchy'),
          groupId: 'hierarchy',
          marryChildren: true,
          children: this.productHierarchyColumns,
        },
        {
          headerName: this.$t('pricing.mainHeaders.attributes', {
            max: this.toggleLogic[maxAdditionalGridViewAttributes],
          }),
          groupId: 'attributes',
          marryChildren: true,
          children: this.attributesColumns,
        },
        ...columns,
      ];
    },

    getValidationColumn() {
      return {
        headerName: this.$t('priceValidations.productBreakingValidation'),
        colId: 'priceValidations',
        pinned: 'left',
        width: 110,
        suppressSizeToFit: true,
        sortable: false,
        cellRenderer: 'PriceValidationBadge',
        valueGetter: params => {
          if (!params.data) return null;
          const validations = this.validationsByProductKey.get(Number(params.data.productKey));
          return validations && validations.length > 0 ? validations : null;
        },
        tooltipValueGetter: params => {
          if (!params.data) return '';
          const validations = this.validationsByProductKey.get(Number(params.data.productKey));
          if (!validations || validations.length === 0) return '';
          return validations
            .map(
              validation =>
                `${this.$t('priceValidations.tooltipPrefix')}: ${
                  validation.ruleDescription
                }. ${this.$t('priceValidations.tooltipSuffix')}`
            )
            .join('\n');
        },
        filter: 'agSetColumnFilter',
        filterValueGetter: params => {
          if (!params.data) return null;
          const validations = this.validationsByProductKey.get(Number(params.data.productKey));
          if (!validations || validations.length === 0) return null;
          return [
            ...new Set(
              validations.map(
                validation =>
                  `${validation.shorthand} [${String(validation.severity || '').toUpperCase()}]`
              )
            ),
          ];
        },
      };
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.gridApi.showLoadingOverlay();

      // Use the pre-created debounced function for all event listeners
      ['firstDataRendered', 'displayedColumnsChanged'].forEach(event =>
        this.gridApi.addEventListener(event, this.debouncedUpdateBoundaries)
      );

      // Add sort event listener to refresh row styles after sorting.
      // Clears stableSortRowOrder so that user-initiated sorts use the current override price
      // rather than the pre-edit row order captured during the last price edit.
      this.gridApi.addEventListener('sortChanged', () => {
        this.stableSortRowOrder = null;
        this.gridApi.refreshCells();
      });

      // Add AG Grid event listeners for selection reconciliation (bulk overrides)
      if (this.isFeatureFlagEnabled(allowBulkPriceOverrides)) {
        // Reconcile on filter changes (AG Grid filters)
        this.gridApi.addEventListener('filterChanged', this.debouncedReconcileSelection);
        // Reconcile on model updates (data changes, grouping, etc.)
        this.gridApi.addEventListener('modelUpdated', this.debouncedReconcileSelection);
      }

      // Track fill handle start/end to batch date range override updates into a single API call.
      if (this.isFeatureFlagEnabled(enableGridViewFillHandle)) {
        this.gridApi.addEventListener('fillStart', () => {
          this.pendingDateFillItems = [];
        });
        this.gridApi.addEventListener('fillEnd', async () => {
          const pendingItems = this.pendingDateFillItems || [];
          this.pendingDateFillItems = null;
          if (!isEmpty(pendingItems)) {
            const itemsWithUnsavedKpiResults = [];
            const rowNodes = [];
            pendingItems.forEach(({ item, rowNode }) => {
              itemsWithUnsavedKpiResults.push(item);
              if (rowNode) rowNodes.push(rowNode);
            });
            await Promise.all([
              this.setBulkUnsavedKpiResults({ itemsWithUnsavedKpiResults }),
              this.updateOverridePrice({ items: itemsWithUnsavedKpiResults }),
            ]);
            this.gridApi.refreshCells({ rowNodes });
          }
        });
      }

      // Set the initial column state based on the active layout or current state, \
      // to avoid bug when going back or opening another page, and returning to the grid view,
      // since it will show all columns by default and follow current layout.
      const baseState = this.activeLayout
        ? this.activeLayout.columnState
        : this.gridApi.getColumnState();

      // Check if there are existing tool store group or chain filters that should be applied
      // This handles the case when the grid is opened after filters have already been applied
      const { hasToolStoreGroupFilter, hasChainFilter } = hasToolStoreGroupOrChainFilters(
        this.retailAttributesFilter
      );

      if (hasToolStoreGroupFilter || hasChainFilter) {
        // Apply tool store group filter priority over any existing column state
        this.$nextTick(async () => {
          const updatedColumnState = await this.updateColumnStateForToolStoreGroupFilter({
            columnState: baseState,
          });

          // If column state was updated, trigger refetch if needed
          if (updatedColumnState) {
            this.updateColumnStateWithOptionalRefetch({
              levelEntryKey: this.levelEntryKey,
              isUnitManagerView: this.isUnitManagerView,
              columnState: updatedColumnState,
            });
          }
        });
      } else {
        this.setCurrentColumnState(baseState);
      }
    },

    // Process column groups and apply boundary classes recursively
    processColumnGroups(columnGroups) {
      if (!columnGroups || !Array.isArray(columnGroups)) return;

      columnGroups.forEach(group => {
        // Skip invalid groups
        if (!group || !group.getDisplayedLeafColumns) return;

        // Get visible leaf columns for this group
        const visibleColumns = group.getDisplayedLeafColumns().filter(col => col.isVisible());

        // Apply boundary classes if we have visible columns
        if (visibleColumns.length > 0) {
          // Clean existing boundary classes from all columns
          visibleColumns.forEach(col => this.cleanBoundaryClasses(col.getColDef()));

          // Add border classes to first and last columns
          this.addBoundaryClasses(visibleColumns[0], 'first');
          this.addBoundaryClasses(visibleColumns[visibleColumns.length - 1], 'last');
        }

        // Recursively process child groups
        if (group.children && group.children.length) {
          this.processColumnGroups(group.children);
        }
      });
    },

    // Updates the border classes for each displayed column group.
    // For each group, it removes any existing border classes from the header and cell,
    // then adds the appropriate "first" and "last" border classes to the first and last visible columns.
    updateColumnGroupBoundaryClasses() {
      if (!this.gridApi) return;

      // Retrieve all displayed column groups
      const allColumnGroups = this.gridApi.getAllDisplayedColumnGroups();

      // Process all column groups
      this.processColumnGroups(allColumnGroups);

      // Refresh the UI to show updated classes
      this.gridApi.refreshHeader();

      // Refresh cells to ensure the cell classes are updated as well
      this.gridApi.refreshCells({ force: true, suppressFlash: true });
    },

    // Helper function to remove specified CSS classes from a given property (headerClass or cellClass)
    cleanBoundaryClasses(colDef) {
      if (!colDef) return;

      // Use a Set for faster lookups
      const borderClassSet = new Set([
        'ag-header-group-cell-first',
        'ag-header-group-cell-last',
        'ag-group-cell-first',
        'ag-group-cell-last',
      ]);

      // Helper to filter classes consistently
      const filterClasses = classes => {
        if (!classes) return classes;
        const classArray = Array.isArray(classes) ? classes : [classes];
        return classArray.filter(cls => !borderClassSet.has(cls));
      };

      // Clean headerClass - currently only handles array case
      // Only handle the array case, with caution about function case
      if (Array.isArray(colDef.headerClass)) {
        if (typeof colDef.headerClass === 'function') {
          // If it's a function that returns class(es)
          const original = colDef.headerClass;
          colDef.headerClass = params => filterClasses(original(params));
        } else {
          // Handle both string and array cases with our helper
          colDef.headerClass = filterClasses(colDef.headerClass);
        }
      }

      // Clean cellClass
      if (colDef.cellClass) {
        if (typeof colDef.cellClass === 'function') {
          // If it's a function that returns class(es)
          const original = colDef.cellClass;
          colDef.cellClass = params => filterClasses(original(params));
        } else {
          // Handle both string and array cases with our helper
          colDef.cellClass = filterClasses(colDef.cellClass);
        }
      }
    },

    // Adds border classes to a column based on its position (either "first" or "last").
    addBoundaryClasses(col, position) {
      const colDef = col.getColDef();
      const headerClass = `ag-header-group-cell-${position}`;
      const cellClass = `ag-group-cell-${position}`;

      // Append the header class.
      if (typeof colDef.headerClass === 'function') {
        const originalHeaderClass = colDef.headerClass;
        colDef.headerClass = params => [...(originalHeaderClass(params) || []), headerClass];
      } else {
        colDef.headerClass = [
          ...(Array.isArray(colDef.headerClass) ? colDef.headerClass : []),
          headerClass,
        ];
      }

      // Append the cell class, preserving any dynamic function if present.
      if (typeof colDef.cellClass === 'function') {
        const originalCellClass = colDef.cellClass;
        colDef.cellClass = params => [...(originalCellClass(params) || []), cellClass];
      } else {
        colDef.cellClass = [
          ...(Array.isArray(colDef.cellClass) ? colDef.cellClass : []),
          cellClass,
        ];
      }
    },

    getRowPrefix(params) {
      if (!this.isGroupTSGsEnabled || !params || !params.data) return '';

      const groupId = get(params, 'column.parent.parent.groupId', '');

      if (isToolStoreGroup(groupId)) {
        const tsgKey = getTsgKeyFromGroupId(groupId);
        return tsgKey ? `toolStoreGroups.${tsgKey}.` : '';
      }

      return '';
    },

    getAlertsColumn({ prefix, tooltipPosition, pinned = null }) {
      return {
        headerName: this.$t('pricing.alerts'),
        field: `${prefix}filteredAlerts`,
        colId: `${prefix}filteredAlerts`,
        width: 62,
        sortable: false,
        pinned,
        cellClass: ['alerts-cell'],
        cellRendererSelector: params =>
          get(params.data, `${prefix}filteredAlerts`)
            ? { component: 'agGridCellRendererComponentWrapper' }
            : '',
        valueGetter: params =>
          get(params.data, `${prefix}filteredAlerts`)
            ? {
                component: 'product-alerts-container',
                props: {
                  product: !prefix ? params.data : get(params.data, `${prefix.slice(0, -1)}`),
                  position: tooltipPosition,
                },
              }
            : '',
        filter: 'agSetColumnFilter',
        filterValueGetter: params =>
          get(params.data, `${prefix}filteredAlerts`, []).map(alert =>
            this.$t(`alerts.${alert.alertType}`)
          ),
      };
    },

    getAdditionalCostColumns(prefix) {
      // Return empty array if feature flag is not enabled
      if (!this.isFeatureFlagEnabled(showAdditionalCostTypes)) {
        return [];
      }

      return [
        {
          headerName: this.$t('pricing.mainHeaders.transferPrice'),
          marryChildren: true,
          children: [
            {
              headerName: this.$t('pricing.liveTransferPrice'),
              field: `${prefix}${this.getPriceFieldObjectPath('liveTransferPrice')}`,
              colId: `${prefix}liveTransferPrice.price`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: ['ag-right-aligned-cell', 'font-weight-bold'],
              valueFormatter: params =>
                this.formatNumber({
                  number: params.value,
                  format: numberFormats.costFormat,
                  nullAsDash: true,
                }),
            },
            {
              headerName: this.$t('pricing.intentionTransferPrice'),
              field: `${prefix}${this.getPriceFieldObjectPath('intentionTransferPrice')}`,
              colId: `${prefix}intentionTransferPrice.price`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: 'ag-right-aligned-cell',
              filterValueGetter: agGridUtils.getDashWhenEqualToRefFilterValueGetter(
                `${prefix}${this.getPriceFieldObjectPath('intentionTransferPrice')}`,
                `${prefix}${this.getPriceFieldObjectPath('liveTransferPrice')}`
              ),
              valueFormatter: params => {
                if (
                  get(
                    params.data,
                    `${prefix}${this.getPriceFieldObjectPath('liveTransferPrice')}`
                  ) !== params.value
                ) {
                  return this.formatNumber({
                    number: params.value,
                    format: numberFormats.costFormat,
                    nullAsDash: true,
                  });
                }
                return '—';
              },
              tooltipValueGetter: this.getTooltipValueGetter({
                dataPath: 'intentionTransferPrice',
                condition: data => this.displayIntentionPriceTooltip(data),
                tooltipFields: {
                  'gridView.popupText.effectiveDate': {
                    path: 'effectiveDate',
                    format: this.formatLocalisedDate,
                  },
                },
              }),
            },
            {
              headerName: this.$t('pricing.transferPriceDelta'),
              field: `${prefix}${this.getPriceField('transferPriceDelta')}`,
              colId: `${prefix}transferPriceDelta`,
              width: 60,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: params => {
                return [this.formatNumberColourClass(-params.value), 'ag-right-aligned-cell'];
              },
              valueFormatter: params =>
                this.formatNumber({
                  number: params.value,
                  format: numberFormats.costFormat,
                  nullAsDash: true,
                  zeroAsDash: true,
                }),
            },
          ],
        },
        {
          headerName: this.$t('pricing.mainHeaders.purchasePrice'),
          marryChildren: true,
          children: [
            {
              headerName: this.$t('pricing.livePurchasePrice'),
              field: `${prefix}${this.getPriceFieldObjectPath('livePurchasePrice')}`,
              colId: `${prefix}livePurchasePrice.price`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: ['ag-right-aligned-cell', 'font-weight-bold'],
              valueFormatter: params =>
                this.formatNumber({
                  number: params.value,
                  format: numberFormats.costFormat,
                  nullAsDash: true,
                }),
            },
            {
              headerName: this.$t('pricing.intentionPurchasePrice'),
              field: `${prefix}${this.getPriceFieldObjectPath('intentionPurchasePrice')}`,
              colId: `${prefix}intentionPurchasePrice.price`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: 'ag-right-aligned-cell',
              filterValueGetter: agGridUtils.getDashWhenEqualToRefFilterValueGetter(
                `${prefix}${this.getPriceFieldObjectPath('intentionPurchasePrice')}`,
                `${prefix}${this.getPriceFieldObjectPath('livePurchasePrice')}`
              ),
              valueFormatter: params => {
                if (
                  get(
                    params.data,
                    `${prefix}${this.getPriceFieldObjectPath('livePurchasePrice')}`
                  ) !== params.value
                ) {
                  return this.formatNumber({
                    number: params.value,
                    format: numberFormats.costFormat,
                    nullAsDash: true,
                  });
                }
                return '—';
              },
              tooltipValueGetter: this.getTooltipValueGetter({
                dataPath: 'intentionPurchasePrice',
                condition: data => this.displayIntentionPriceTooltip(data),
                tooltipFields: {
                  'gridView.popupText.effectiveDate': {
                    path: 'effectiveDate',
                    format: this.formatLocalisedDate,
                  },
                },
              }),
            },
            {
              headerName: this.$t('pricing.purchasePriceDelta'),
              field: `${prefix}${this.getPriceField('purchasePriceDelta')}`,
              colId: `${prefix}purchasePriceDelta`,
              width: 60,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: params => {
                return [this.formatNumberColourClass(-params.value), 'ag-right-aligned-cell'];
              },
              valueFormatter: params =>
                this.formatNumber({
                  number: params.value,
                  format: numberFormats.costFormat,
                  nullAsDash: true,
                  zeroAsDash: true,
                }),
            },
          ],
        },
      ];
    },

    // Format tooltip data with newlines rather than HTML
    formatTooltipText(data) {
      if (!data || Object.keys(data).length === 0) return null;

      return Object.entries(data)
        .filter(([, value]) => value !== null && value !== undefined)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n');
    },

    applyGridColumnState(columnState) {
      if (!this.gridApi || !columnState || !Array.isArray(columnState)) {
        return;
      }

      this.gridApi.applyColumnState({
        state: columnState,
        defaultState: {
          hide: true,
        },
        applyOrder: true,
        applyVisible: true,
        applySize: false,
        applySort: false,
        applyFilter: false,
      });
    },

    getColumns(tsgKey) {
      const prefix = this.isGroupTSGsEnabled && tsgKey ? `toolStoreGroups.${tsgKey}.` : '';

      const showHistoricalCompetitorPrices = this.toggleLogic[displayCompetitorHistoricalPrices];

      const competitorColumns = flatMap(this.nonStoreGroupCompetitors, (c, i) => {
        const competitorKey = c.competitorKey;

        return [
          // 1. Price
          {
            headerName: `${c.competitorDisplayDescription} ${this.$t('pricing.competitorPrice')}`,
            field: `${prefix}${
              this.readFromDatabricks
                ? `competitors.${competitorKey}.${this.getPriceField('competitorPrice')}`
                : `competitor${i + 1}.${this.getPriceField('competitorPrice')}`
            }`,
            colId: `${prefix}competitors.${competitorKey}.competitorPrice`,
            filter: 'agMultiColumnFilter',
            filterParams: agGridUtils.getMultiNumericFilterParams(),
            cellDataType: 'number',
            cellClass: ['ag-right-aligned-cell', 'font-weight-bold'],
            valueFormatter: params =>
              this.formatNumberEmptyCheck(params, 'value', numberFormats.priceFormat),
            // Use text-based tooltip with newlines
            tooltipValueGetter: this.getTooltipValueGetter({
              dataPath: this.readFromDatabricks
                ? `competitors.${competitorKey}`
                : `competitor${i + 1}`,
              tooltipFields: {
                'tooltip.competitorDescription': { path: 'competitorProductDescription' },
                'tooltip.competitorSize': {
                  path: 'contentValue',
                  format: numberFormats.priceFormat,
                  zeroAsDash: true,
                  nullAsDash: true,
                },
                'tooltip.competitorUnitOfMeasure': { path: 'contentUnitOfMeasure' },
                'tooltip.competitorPrice': {
                  path: 'competitorPrice',
                  format: numberFormats.priceFormat,
                  skipIfNull: true,
                },
                'tooltip.grossReferenceCompetitorPrice': {
                  path: 'grossReferenceCompetitorPrice',
                  format: numberFormats.priceFormat,
                  skipIfNull: true,
                },
                'tooltip.avgCompetitorPrice': {
                  path: 'avgCompetitorPrice',
                  format: numberFormats.priceFormat,
                  skipIfNull: true,
                },
                'tooltip.medianShelfCompetitorPrice': {
                  path: 'medianShelfCompetitorPrice',
                  format: numberFormats.priceFormat,
                  skipIfNull: true,
                },
                'tooltip.minCompetitorPrice': {
                  path: 'minCompetitorPrice',
                  format: numberFormats.priceFormat,
                  skipIfNull: true,
                },
                'tooltip.maxCompetitorPrice': {
                  path: 'maxCompetitorPrice',
                  format: numberFormats.priceFormat,
                  skipIfNull: true,
                },
              },
            }),
          },
          // 2. Index
          {
            headerName: `${c.competitorDisplayDescription} ${this.$t('pricing.competitorIndex')}`,
            field: `${prefix}${
              this.readFromDatabricks
                ? `competitors.${competitorKey}.competitorIndex`
                : `competitor${i + 1}.competitorIndex`
            }`,
            colId: `${prefix}competitors.${competitorKey}.competitorIndex`,
            filter: 'agMultiColumnFilter',
            filterParams: agGridUtils.getMultiNumericFilterParams(),
            cellDataType: 'number',
            cellClass: params => {
              const compIndex = this.toggleLogic[useAbsoluteCompetitorIndex]
                ? params.value - 100
                : params.value;
              return [this.formatNumberColourClass(compIndex), 'ag-right-aligned-cell'];
            },
            valueFormatter: params => {
              const format = this.toggleLogic[useAbsoluteCompetitorIndex]
                ? numberFormats.priceFormat
                : numberFormats.percent;
              return this.formatNumberEmptyCheck(params, 'value', format);
            },
          },
          // 3. Historical prices (Last 2 wks, Last wk, This wk)
          ...(c.competitorType === 'feed' && showHistoricalCompetitorPrices
            ? [
                'competitorPriceTwoWeeksAgo',
                'competitorPriceLastWeek',
                'competitorPriceThisWeek',
              ].map((colName, idx) => ({
                headerName: `${c.competitorDisplayDescription} ${this.$t(`pricing.${colName}`)}`,
                colId: `${prefix}competitors.${competitorKey}.${colName}`,
                filter: 'agMultiColumnFilter',
                filterParams: agGridUtils.getMultiNumericFilterParams(),
                cellDataType: 'number',
                cellClass: ['ag-right-aligned-cell'],
                valueGetter: params => {
                  const history = get(
                    params.data,
                    `${prefix}competitors.${competitorKey}.priceHistoryWeek`,
                    []
                  );
                  const recentHistory = history.slice(-3);
                  return get(recentHistory, idx, null);
                },
                valueFormatter: params =>
                  this.formatNumberEmptyCheck(params, 'value', numberFormats.priceFormat),
              }))
            : []),
          // 4. 12wk Price Change sparkline
          {
            headerName: `${c.competitorDisplayDescription} ${this.$t('pricing.weekPriceChange')}`,
            colId: `${prefix}competitors.${competitorKey}.weekPriceChange`,
            width: 72,
            sortable: false,
            suppressHeaderMenuButton: true,
            cellRenderer: 'agGridCellRendererComponentWrapper',
            valueGetter: params => ({
              component: 'sparkline-chart',
              props: {
                priceHistoryWeek: get(
                  params.data,
                  `${prefix}competitors.${competitorKey}.priceHistoryWeek`
                ),
              },
            }),
          },
          // 5. Feed Price
          ...(c.competitorType === 'feed'
            ? [
                {
                  headerName: `${c.competitorDisplayDescription} ${this.$t(
                    'pricing.competitorFeedPrice'
                  )}`,
                  field: `${prefix}competitors.${competitorKey}.competitorFeedPrice`,
                  colId: `${prefix}competitors.${competitorKey}.competitorFeedPrice`,
                  filter: 'agMultiColumnFilter',
                  filterParams: agGridUtils.getMultiNumericFilterParams(),
                  cellDataType: 'number',
                  cellClass: ['ag-right-aligned-cell'],
                  valueFormatter: params =>
                    this.formatNumberEmptyCheck(params, 'value', numberFormats.priceFormat),
                },
                // 6. Delta vs Reference Date Price
                ...(this.enableReferenceDate
                  ? [
                      {
                        headerName: `${c.competitorDisplayDescription} ${this.$t(
                          'pricing.competitorDeltaVsReferenceDatePrice'
                        )}`,
                        colId: `${prefix}competitors.${competitorKey}.competitorDeltaVsReferenceDatePrice`,
                        valueGetter: params => {
                          const competitorData = get(
                            params.data,
                            `${prefix}competitors.${competitorKey}`
                          );
                          const competitorFeedPrice = get(competitorData, 'competitorFeedPrice');
                          const competitorReferenceDatePrice = get(
                            competitorData,
                            'competitorReferenceDatePrice'
                          );
                          if (isNil(competitorFeedPrice) || isNil(competitorReferenceDatePrice)) {
                            return null;
                          }
                          return round(
                            (competitorFeedPrice - competitorReferenceDatePrice) /
                              competitorReferenceDatePrice,
                            4
                          );
                        },
                        filter: 'agMultiColumnFilter',
                        filterParams: agGridUtils.getMultiNumericFilterParams(),
                        cellDataType: 'number',
                        cellClass: params => [
                          this.formatNumberColourClass(params.value),
                          'ag-right-aligned-cell',
                        ],
                        valueFormatter: params =>
                          this.formatNumberEmptyCheck(params, 'value', numberFormats.percent),
                      },
                    ]
                  : []),
              ]
            : []),
          // 7. Unsimulated Index
          ...(this.isFeatureFlagEnabled(showUnsimulatedIndex)
            ? [
                {
                  headerName: this.$t('pricing.unsimulatedCompetitorIndex', {
                    competitorName: c.competitorDisplayDescription,
                  }),
                  field: `${prefix}competitors.${competitorKey}.liveCompetitorDistanceScenario`,
                  colId: `${prefix}competitors.${competitorKey}.competitorIndexUnsimulated`,
                  filter: 'agMultiColumnFilter',
                  filterParams: agGridUtils.getMultiNumericFilterParams(),
                  cellDataType: 'number',
                  cellClass: params => {
                    const compIndex = this.toggleLogic[useAbsoluteCompetitorIndex]
                      ? params.value - 100
                      : params.value;
                    return [this.formatNumberColourClass(compIndex), 'ag-right-aligned-cell'];
                  },
                  valueFormatter: params => {
                    const format = this.toggleLogic[useAbsoluteCompetitorIndex]
                      ? numberFormats.priceFormat
                      : numberFormats.percent;
                    return this.formatNumberEmptyCheck(params, 'value', format);
                  },
                },
              ]
            : []),
          // 8. Promotion
          ...(c.competitorType === 'feed' && this.isFeatureFlagEnabled(displayCompetitorPromotion)
            ? [
                {
                  headerName: `${c.competitorDisplayDescription} ${this.$t(
                    'pricing.competitorPromotion'
                  )}`,
                  field: `${prefix}competitors.${competitorKey}.competitorPromotion`,
                  tooltipField: `${prefix}competitors.${competitorKey}.competitorPromotion`,
                  colId: `${prefix}competitors.${competitorKey}.competitorPromotion`,
                  cellDataType: 'text',
                },
              ]
            : []),
          // 9. Number Stores Measured
          {
            headerName: `${c.competitorDisplayDescription} ${this.$t(
              'pricing.numberStoresMeasured'
            )}`,
            field: `${prefix}${
              this.readFromDatabricks
                ? `competitors.${competitorKey}.numberStoresMeasured`
                : `competitor${i + 1}.numberStoresMeasured`
            }`,
            colId: `${prefix}competitors.${competitorKey}.numberStoresMeasured`,
            filter: 'agMultiColumnFilter',
            filterParams: agGridUtils.getMultiNumericFilterParams(),
            cellDataType: 'number',
            valueFormatter: params =>
              this.formatNumber({
                number: params.value,
                format: numberFormats.integer,
                nullAsDash: true,
                zeroAsDash: true,
              }),
          },
        ];
      });

      const columns = [
        {
          headerName: this.$t('gridView.linePricingGroup'),
          groupId: `${prefix}LPG`,
          marryChildren: true,
          hide: true,
          children: [
            {
              headerName: this.$t('gridView.linePricingGroup'),
              field: `${prefix}linePricingGroupDescription`,
              colId: `${prefix}linePricingGroupDescription`,
              width: 100,
            },
          ],
        },
        {
          headerName: this.$t('pricing.mainHeaders.sales'),
          marryChildren: true,
          children: [
            {
              headerName: this.$t(
                `pricing.${
                  this.isFeatureFlagEnabled(useForecastVolume) ? 'historicSales' : 'sales'
                }`
              ),
              field: `${prefix}yearlySales`,
              colId: `${prefix}yearlySales`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: ['ag-right-aligned-cell'],
              valueFormatter: params =>
                !isNil(params.value)
                  ? this.formatNumber({
                      number: params.value / 1000,
                      format: numberFormats.oneDecimalPlace,
                    })
                  : '-',
            },
            {
              headerName: this.$t('pricing.forecastSales'),
              field: `${prefix}forecastSales`,
              colId: `${prefix}forecastSales`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: ['ag-right-aligned-cell'],
              ...this.getColumnVisibilityState(useForecastVolume),
              suppressColumnsToolPanel: !this.isFeatureFlagEnabled(useForecastVolume),
              valueFormatter: params =>
                !isNil(params.value)
                  ? this.formatNumber({
                      number: params.value / 1000,
                      format: numberFormats.oneDecimalPlace,
                    })
                  : '-',
            },
            {
              headerName: this.$t(
                `pricing.${
                  this.isFeatureFlagEnabled(useForecastVolume) ? 'historicVolume' : 'volume'
                }`
              ),
              field: `${prefix}yearlyVolume`,
              colId: `${prefix}yearlyVolume`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: 'ag-right-aligned-cell',
              valueFormatter: params =>
                !isNil(params.value)
                  ? this.formatNumber({
                      number: params.value / 1000,
                      format: numberFormats.oneDecimalPlace,
                    })
                  : '-',
            },
            {
              headerName: this.$t('pricing.forecastVolume'),
              field: `${prefix}forecastVolume`,
              colId: `${prefix}forecastVolume`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: 'ag-right-aligned-cell',
              ...this.getColumnVisibilityState(useForecastVolume),
              suppressColumnsToolPanel: !this.isFeatureFlagEnabled(useForecastVolume),
              valueFormatter: params =>
                !isNil(params.value)
                  ? this.formatNumber({
                      number: params.value / 1000,
                      format: numberFormats.oneDecimalPlace,
                    })
                  : '-',
            },
            {
              headerName: this.$t('pricing.staticForecastVolume'),
              field: `${prefix}staticForecastVolume`,
              colId: `${prefix}staticForecastVolume`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: 'ag-right-aligned-cell',
              sort:
                !tsgKey && !this.isUnitManagerView && this.isFeatureFlagEnabled(useForecastVolume)
                  ? 'desc'
                  : undefined,
              sortIndex:
                !tsgKey && !this.isUnitManagerView && this.isFeatureFlagEnabled(useForecastVolume)
                  ? 0
                  : undefined,
              ...this.getColumnVisibilityState(useForecastVolume),
              suppressColumnsToolPanel: !this.isFeatureFlagEnabled(useForecastVolume),
              valueFormatter: params =>
                !isNil(params.value)
                  ? this.formatNumber({
                      number: params.value / 1000,
                      format: numberFormats.oneDecimalPlace,
                    })
                  : '-',
            },
            {
              headerName: this.$t('pricing.strategicForecastVolume'),
              field: `${prefix}strategicForecastVolume`,
              colId: `${prefix}strategicForecastVolume`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: 'ag-right-aligned-cell',
              ...this.getColumnVisibilityState(enablePriceOptimisation),
              suppressColumnsToolPanel: !this.isFeatureFlagEnabled(enablePriceOptimisation),
              valueFormatter: params =>
                !isNil(params.value)
                  ? this.formatNumber({
                      number: params.value / 1000,
                      format: numberFormats.oneDecimalPlace,
                    })
                  : '-',
            },
            {
              headerName: this.$t('pricing.crossElasticityVolume'),
              field: `${prefix}crossElasticityVolume`,
              colId: `${prefix}crossElasticityVolume`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: 'ag-right-aligned-cell',
              ...this.getColumnVisibilityState(enablePriceOptimisation),
              suppressColumnsToolPanel: !this.isFeatureFlagEnabled(enablePriceOptimisation),
              valueFormatter: params =>
                !isNil(params.value)
                  ? this.formatNumber({
                      number: params.value / 1000,
                      format: numberFormats.oneDecimalPlace,
                    })
                  : '-',
            },
            {
              headerName: this.$t('pricing.promo'),
              field: `${prefix}mandatoryEngineInputs.promoParticipationSales`,
              colId: `${prefix}mandatoryEngineInputs.promoParticipationSales`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: 'ag-right-aligned-cell',
              valueFormatter: params =>
                this.formatNumber({
                  number: this.formatEngineInput(params.value),
                  format: numberFormats.percent,
                  nullAsDash: true,
                }),
              // Use text-based tooltip with newlines
              tooltipValueGetter: this.getTooltipValueGetter({
                dataPath: 'mandatoryEngineInputs',
                tooltipFields: {
                  'gridView.popupText.promoParticipationSales': {
                    path: 'promoParticipationSales',
                    format: numberFormats.percent,
                    nullAsDash: true,
                    dividePercentBy100: true,
                  },
                  'gridView.popupText.promoDiscount': {
                    path: 'promoDiscount',
                    format: numberFormats.percent,
                    nullAsDash: true,
                    dividePercentBy100: true,
                  },
                  'gridView.popupText.promoFunding': {
                    path: 'promoFunding',
                    format: numberFormats.percent,
                    nullAsDash: true,
                    dividePercentBy100: true,
                  },
                },
              }),
            },
          ],
        },
        {
          headerName: this.$t('pricing.mainHeaders.cost'),
          marryChildren: true,
          children: [
            {
              headerName: this.$t('pricing.liveCost'),
              field: `${prefix}${this.liveCostPath}`,
              colId: `${prefix}liveCost`,
              width: 60,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: ['ag-right-aligned-cell'],
              valueFormatter: params =>
                this.formatNumber({
                  number: params.value,
                  format: numberFormats.costFormat,
                  nullAsDash: true,
                }),
            },
            {
              headerName: this.$t('pricing.goLiveCost'),
              field: `${prefix}${this.getPriceField('nonPromoNetCost')}`,
              colId: `${prefix}nonPromoNetCost`,
              width: 60,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: ['ag-right-aligned-cell'],
              valueFormatter: params =>
                this.formatNumber({
                  number: params.value,
                  format: numberFormats.costFormat,
                  nullAsDash: true,
                }),
            },
            {
              headerName: this.$t('pricing.costDelta'),
              headerTooltip: this.$t('pricing.costDeltaExplanation'),
              field: `${prefix}${this.getPriceField('costDelta')}`,
              colId: `${prefix}costDelta`,
              width: 60,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: params => {
                return [this.formatNumberColourClass(-params.value), 'ag-right-aligned-cell'];
              },
              valueFormatter: params =>
                this.formatNumber({
                  number: params.value,
                  format: numberFormats.costFormat,
                  nullAsDash: true,
                  zeroAsDash: true,
                }),
            },
            {
              headerName: this.$t('pricing.costDeltaPercent'),
              headerTooltip: this.$t('pricing.costDeltaPercentExplanation'),
              colId: `${prefix}costDeltaPercent`,
              width: 60,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: ['ag-right-aligned-cell'],
              // no field, this is a derived column
              valueGetter: params => this.getCostDeltaPercentage(params, prefix),
              // Filter by the displayed percentage value (e.g. 12.76 instead of 0.1275512),
              // rounded to 2 decimal places to match the percentWithExtraDecimalPlaces format
              filterValueGetter: params => {
                const value = this.getCostDeltaPercentage(params, prefix);
                return isNil(value) ? null : round(value * 100, 2);
              },
              valueFormatter: params =>
                this.formatNumber({
                  number: params.value,
                  format: numberFormats.percentWithExtraDecimalPlaces,
                  nullAsDash: true,
                  zeroAsDash: true,
                }),
            },
            // only stored as single value for now, i.e. no normWeight vs uom values so no need for getPriceField
            ...(this.enableReferenceDate
              ? [
                  {
                    headerName: this.$t('pricing.referenceDateCost'),
                    field: `${prefix}referenceDateCost.price`,
                    colId: `${prefix}referenceDateCost.price`,
                    width: 90,
                    filter: 'agMultiColumnFilter',
                    filterParams: agGridUtils.getMultiNumericFilterParams(),
                    cellDataType: 'number',
                    cellClass: ['ag-right-aligned-cell'],
                    valueFormatter: params =>
                      this.formatNumber({
                        number: params.value,
                        format: numberFormats.costFormat,
                        nullAsDash: true,
                      }),
                  },
                  {
                    headerName: this.$t('pricing.referenceDateCostDelta'),
                    headerTooltip: this.$t('pricing.referenceDateCostDeltaExplanation'),
                    colId: `${prefix}referenceDateCostDelta`,
                    width: 90,
                    filter: 'agMultiColumnFilter',
                    filterParams: agGridUtils.getMultiNumericFilterParams(),
                    cellDataType: 'number',
                    cellClass: ['ag-right-aligned-cell'],
                    // no field, this is a derived column
                    valueGetter: params => {
                      // referenceDateCost does not have UOM / NormWeight values, so read default cost too for comparison
                      const goLiveCost = get(params, `data.${prefix}nonPromoNetCost`);
                      const referenceDateCost = get(
                        params,
                        `data.${prefix}referenceDateCost.price`
                      );
                      if (isNil(goLiveCost) || isNil(referenceDateCost)) {
                        return null;
                      }
                      return goLiveCost - referenceDateCost;
                    },
                    valueFormatter: params =>
                      this.formatNumber({
                        number: params.value,
                        format: numberFormats.costFormat,
                        nullAsDash: true,
                      }),
                  },
                ]
              : []),
          ],
        },
        ...this.getAdditionalCostColumns(prefix),
        {
          headerName: this.$t('pricing.mainHeaders.prices'),
          marryChildren: true,
          children: [
            {
              headerName: this.$t('pricing.weekPriceChange'),
              width: 72,
              sortable: false,
              suppressHeaderMenuButton: true,
              colId: `${prefix}weekPriceChange`,
              cellRenderer: 'agGridCellRendererComponentWrapper',
              valueGetter: params => ({
                component: 'sparkline-chart',
                props: {
                  priceHistoryWeek: get(params.data, `${prefix}priceHistoryWeek`),
                  intentionPrice: get(params.data, `${prefix}intentionPrice.price`),
                },
              }),
            },
            {
              headerName: this.$t('pricing.livePrice'),
              field: `${prefix}${this.livePricePath}`,
              colId: `${prefix}livePrice`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: ['ag-right-aligned-cell', 'font-weight-bold'],
              valueFormatter: params =>
                this.formatNumber({
                  number: params.value,
                  format: numberFormats.priceFormat,
                  nullAsDash: true,
                }),
            },
            {
              headerName: this.$t('pricing.intentionPrice'),
              field: `${prefix}${this.getPriceFieldObjectPath('intentionPrice')}`,
              colId: `${prefix}intentionPrice.price`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: 'ag-right-aligned-cell',
              filterValueGetter: agGridUtils.getDashWhenEqualToRefFilterValueGetter(
                `${prefix}${this.getPriceFieldObjectPath('intentionPrice')}`,
                `${prefix}${this.livePricePath}`
              ),
              valueFormatter: params => {
                if (get(params.data, `${prefix}${this.livePricePath}`) !== params.value) {
                  return this.formatNumber({
                    number: params.value,
                    format: numberFormats.priceFormat,
                    zeroAsDash: true,
                    nullAsDash: true,
                  });
                }
                return '—';
              },
              // Use text-based tooltip with newlines
              tooltipValueGetter: this.getTooltipValueGetter({
                dataPath: 'intentionPrice',
                condition: data => this.displayIntentionPriceTooltip(data),
                tooltipFields: {
                  'gridView.popupText.effectiveDate': {
                    path: 'effectiveDate',
                    format: this.formatLocalisedDate,
                  },
                },
              }),
            },
            {
              ...(() => {
                const isLiveMargin = this.toggleLogic[marginComparisonType] === 'live';
                const marginType = isLiveMargin ? 'live' : 'intention';
                return {
                  headerName: this.$t(`pricing.${marginType}Margin`),
                  field: `${prefix}${marginType}Margin`,
                  colId: `${prefix}${marginType}Margin`,
                };
              })(),
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              cellClass: ['ag-right-aligned-cell', 'font-weight-bold'],
              valueFormatter: params =>
                this.formatNumber({
                  number: params.value,
                  format: numberFormats.percent,
                  nullAsDash: true,
                }),
            },

            // only stored as single value for now, i.e. no normWeight vs uom values so no need for getPriceField
            ...(this.enableReferenceDate
              ? [
                  {
                    headerName: this.$t('pricing.referenceDatePrice'),
                    field: `${prefix}referenceDatePrice.price`,
                    colId: `${prefix}referenceDatePrice.price`,
                    width: 60,
                    filter: 'agMultiColumnFilter',
                    filterParams: agGridUtils.getMultiNumericFilterParams(),
                    cellDataType: 'number',
                    cellClass: ['ag-right-aligned-cell'],
                    valueFormatter: params =>
                      this.formatNumber({
                        number: params.value,
                        format: numberFormats.priceFormat,
                        nullAsDash: true,
                      }),
                  },
                ]
              : []),
          ],
        },
        {
          headerName: this.$t('pricing.mainHeaders.proposedPricing'),
          headerGroupComponent: 'toggleHeader',
          headerGroupComponentParams: {
            headerName: this.$t('pricing.mainHeaders.proposedPricing'),
            headerClass: ['proposed-pricing-header-group'],
            enableToggle: !this.isUnitManagerView && this.toggleLogic[allowStoreGroupPriceLocking],
            toggleLabel: this.$t('actions.lock'),
            toggleIcon: 'mdi-lock',
            tsgKey,
            isDisabled: () => this.itemsNotReadyForDisplay || this.calculatingScenarioResults,
            initialValue: params => {
              return this.lockedPricesByTsgKey[params.tsgKey];
            },
            tooltipValue: params => {
              const lastUpdate = this.lockedPricesDateByTsgKey[params.tsgKey];
              return lastUpdate
                ? this.$t('gridView.lastUpdate', {
                    date: moment(lastUpdate).format(hoursMinutesDayMonthYearSeparatedFormat),
                  })
                : null;
            },
            onToggle: async locked => {
              // Update locked prices flag for TSG
              await this.updateLockedPrices({
                toolStoreGroupKey: tsgKey,
                lockedPrices: locked,
              });
              // Refresh all proposedPricing children columns (including selection) so that
              // styling and disabled state are recalculated from lockedPricesByTsgKey
              this.gridApi.refreshHeader();
              this.gridApi.refreshCells({
                force: true,
                columns: [
                  `${prefix}selection`,
                  `${prefix}${this.getPriceField('scenarioPrice')}`,
                  `${prefix}scenarioPricePerContentUnit`,
                  `${prefix}overridePrice.referencePriceAtOverride`,
                  `${prefix}effectiveDateRange`,
                  `${prefix}${this.getPriceField('scenarioPriceChange')}`,
                  `${prefix}${this.getPriceField('scenarioPriceChangeRatio')}`,
                  `${prefix}scenarioMargin`,
                  `${prefix}${
                    this.showRegularImpact ? 'regularSalesImpact' : 'scenarioSalesImpact'
                  }`,
                  `${prefix}${this.showRegularImpact ? 'regularCostImpact' : 'scenarioCostImpact'}`,
                ],
              });
            },
          },
          marryChildren: true,
          children: [
            ...(this.isFeatureFlagEnabled(allowBulkPriceOverrides)
              ? [
                  {
                    headerName: this.$t('pricing.selection'),
                    colId: `${prefix}selection`,
                    width: 40,
                    suppressHeaderMenuButton: true,
                    pinned: false,
                    headerClass: () => [
                      'proposed-pricing-header-cell',
                      this.isSelectionDisabled(tsgKey) ? 'disabled' : '',
                    ],
                    cellClass: () => [
                      'proposed-pricing-cell',
                      this.isSelectionDisabled(tsgKey) ? 'disabled' : '',
                    ],
                    cellRenderer: 'TsgScopedCheckboxCellRenderer',
                    headerComponent: 'TsgScopedHeaderCheckbox',
                    // Provide TSG context and disabled predicate to renderer components
                    context: {
                      visibilityGroup: `scenarioSelection::${tsgKey || null}`,
                      tsgKey: tsgKey || null,
                    },
                    cellRendererParams: () => {
                      const disabled = this.isSelectionDisabled(tsgKey);
                      return { disabled };
                    },
                    headerComponentParams: () => {
                      const disabled = this.isSelectionDisabled(tsgKey);
                      return { disabled };
                    },
                  },
                ]
              : []),
            {
              headerName: this.$t('pricing.scenarioPrice'),
              headerClass: params => [
                'proposed-pricing-header-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
              ],
              colId: `${prefix}scenarioPrice`,
              width: 100,
              sortable: true,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              filterValueGetter: params => {
                // Return a dash string when the cell displays a dash (same-as-chain price),
                // so the set filter exposes '—' as a selectable option.
                if (this.isTsgPriceMatchingChain(params, tsgKey)) {
                  return '—';
                }
                const itemData = this.getItemData(params);
                return get(itemData, this.getPriceField('scenarioPrice'));
              },
              cellDataType: 'number',
              context: {
                tsgKey: tsgKey || null,
                visibilityGroup: `scenarioSelection::${tsgKey || null}`,
              },
              // Custom comparator for sorting.
              // When nodeA/nodeB are absent (agSetColumnFilter calls the comparator without nodes
              // to sort its checkbox value list), falls back to the raw filterValueGetter values.
              comparator: (valueA, valueB, nodeA, nodeB) => {
                // Returns the price to compare. When no node is available (set filter context),
                // falls back to the raw filter value; '—' is treated as null so it sorts last.
                const getDisplayedPrice = (node, fallbackValue) => {
                  if (!node || !node.data) {
                    return fallbackValue === '—' ? null : fallbackValue;
                  }
                  const itemData = this.getItemData({
                    data: node.data,
                    colDef: { context: { tsgKey } },
                  });
                  if (!itemData) return null;
                  // Sort by the displayed price: override if active, otherwise the engine-calculated scenarioPrice.
                  return this.getOverridePrice(itemData);
                };

                const priceA = getDisplayedPrice(nodeA, valueA);
                const priceB = getDisplayedPrice(nodeB, valueB);

                // Handle null/undefined values
                if (priceA == null && priceB == null) return 0;
                if (priceA == null) return -1;
                if (priceB == null) return 1;

                // Convert to numbers if they're strings using the localized parser
                const numA =
                  typeof priceA === 'string' ? this.formatStringToNumber(priceA) : priceA;
                const numB =
                  typeof priceB === 'string' ? this.formatStringToNumber(priceB) : priceB;

                // Handle NaN values
                if (Number.isNaN(numA) && Number.isNaN(numB)) return 0;
                if (Number.isNaN(numA)) return -1;
                if (Number.isNaN(numB)) return 1;

                return numA - numB;
              },
              cellClass: params => [
                'ag-right-aligned-cell',
                'proposed-pricing-cell',
                this.isValidationFailureCell(params) ? 'validation-failure-cell' : '',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
                'font-weight-bold',
                get(this.getItemData(params), 'hasUnsavedKpiResults') &&
                get(this.getItemData(params), 'hasPriceChanged')
                  ? 'unsaved-kpis'
                  : '',
              ],
              // Custom renderer for display
              cellRenderer: 'agGridCellRendererComponentWrapper',
              valueGetter: params => {
                return {
                  component: 'grid-view-price-override-cell-renderer',
                  props: {
                    valueParams: params,
                    isSmartOverride: params.context.componentParent.isSmartOverride,
                    useOverridePrice: params.context.componentParent.useOverridePrice,
                    hasOverride: params.context.componentParent.hasOverride,
                    getOverridePrice: params.context.componentParent.getOverridePrice,
                    handleRevertButtonClick: params.context.componentParent.handleRevertButtonClick,
                    recalculateKPIWrapper: params.context.componentParent.recalculateKPIWrapper,
                    isEditable:
                      params.context.componentParent.isPriceOverrideEditable &&
                      !this.lockedPricesByTsgKey[params.colDef.context.tsgKey],
                    priceField: this.getPriceField('scenarioPrice'),
                    isActiveTransitionPeriodOverride:
                      params.context.componentParent.isActiveTransitionPeriodOverride,
                    isSameAsChainPrice: params.context.componentParent.isTsgPriceMatchingChain(
                      params,
                      tsgKey
                    ),
                  },
                };
              },
              // Provide raw numeric value for sorting while keeping complex valueGetter for display
              cellEditorSelector: this.scenarioPriceCellEditorSelector,
              // TODO: PRICE-5765 validation missing greaterThan0
              editable: params =>
                params.context.componentParent.isPriceOverrideEditable &&
                !this.lockedPricesByTsgKey[params.colDef.context.tsgKey],
              onCellDoubleClicked: this.handleScenarioPriceCellDoubleClick,
              valueSetter: this.handleScenarioPriceValueSetter,
              onCellValueChanged: this.handleScenarioPriceCellValueChanged,
              suppressFillHandle: true,
              tooltipValueGetter: params => {
                const itemData = this.getItemData(params);
                if (!itemData) return null;

                return this.isActiveTransitionPeriodOverride(itemData)
                  ? this.$t('gridView.tooltips.transitionPeriodOverride')
                  : null;
              },
            },
            {
              headerName: this.$t('pricing.proposedPriceUom'),
              headerClass: params => [
                'proposed-pricing-header-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
              ],
              field: `${prefix}scenarioPricePerContentUnit`,
              colId: `${prefix}scenarioPricePerContentUnit`,
              width: 80,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              context: {
                tsgKey: tsgKey || null,
              },
              cellClass: params => [
                'ag-right-aligned-cell',
                'proposed-pricing-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
              ],
              valueFormatter: params =>
                this.formatNumberEmptyCheck(params, 'value', numberFormats.priceFormat, true),
            },
            {
              headerName: this.$t('pricing.referencePriceAtOverride'),
              headerClass: params => [
                'proposed-pricing-header-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
              ],
              field: `${prefix}overridePrice.referencePriceAtOverride`,
              colId: `${prefix}overridePrice.referencePriceAtOverride`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              filterValueGetter: params => {
                const itemData = this.getItemData(params);
                return params.context.componentParent.getSavedReferencePriceAtOverride(itemData);
              },
              cellDataType: 'number',
              context: {
                tsgKey: tsgKey || null,
                visibilityGroup: `referencePrice::${tsgKey || 'none'}`,
              },
              cellClass: params => [
                'ag-right-aligned-cell',
                'proposed-pricing-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
                get(this.getItemData(params), 'hasUnsavedKpiResults') &&
                get(this.getItemData(params), 'hasPriceChanged') &&
                get(this.getItemData(params), 'overridePrice.referencePriceAtOverride')
                  ? 'unsaved-kpis'
                  : '',
              ],
              valueFormatter: params => {
                const itemData = this.getItemData(params);

                // Only show reference price when override price is in use
                if (params.context.componentParent.useOverridePrice(itemData)) {
                  const referenceValue = params.context.componentParent.getSavedReferencePriceAtOverride(
                    itemData
                  );
                  return this.formatNumber({
                    number: referenceValue,
                    format: numberFormats.priceFormat,
                    nullAsDash: true,
                  });
                }
                return '';
              },
            },
            {
              headerName: this.$t('pricing.strategicPrice'),
              headerClass: params => [
                'proposed-pricing-header-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
              ],
              // displays optimized price, ignoring any overrides/locks
              field: `${prefix}optimizedPrice`,
              colId: `${prefix}optimizedPrice`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              ...this.getColumnVisibilityState(enablePriceOptimisation),
              suppressColumnsToolPanel: !this.isFeatureFlagEnabled(enablePriceOptimisation),
              cellDataType: 'number',
              context: {
                tsgKey: tsgKey || null,
                visibilityGroup: `optimizedPrice::${tsgKey || 'none'}`,
              },
              cellClass: params => [
                'ag-right-aligned-cell',
                'proposed-pricing-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
              ],
              valueFormatter: params =>
                this.formatNumberEmptyCheck(params, 'value', numberFormats.priceFormat, true),
            },
            {
              headerName: this.$t('pricing.crossElasticityPrice'),
              headerClass: params => [
                'proposed-pricing-header-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
              ],
              field: `${prefix}crossElasticityPrice`,
              colId: `${prefix}crossElasticityPrice`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              ...this.getColumnVisibilityState(enablePriceOptimisation),
              suppressColumnsToolPanel: !this.isFeatureFlagEnabled(enablePriceOptimisation),
              cellDataType: 'number',
              context: {
                tsgKey: tsgKey || null,
                visibilityGroup: `crossElasticityPrice::${tsgKey || 'none'}`,
              },
              cellClass: params => [
                'ag-right-aligned-cell',
                'proposed-pricing-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
              ],
              valueFormatter: params =>
                this.formatNumberEmptyCheck(params, 'value', numberFormats.priceFormat, true),
            },
            {
              headerName: this.$t('pricing.overrideDateRange'),
              headerClass: params => [
                'proposed-pricing-header-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
              ],
              field: `${prefix}effectiveDateRange`,
              colId: `${prefix}effectiveDateRange`,
              width: 100,
              filter: 'agTextColumnFilter',
              filterValueGetter: this.formatEffectiveDateRange,
              valueGetter: params => {
                // Returns a structured object so the default fill handle can copy the date
                // lock to other rows without needing a custom fillOperation.
                const itemData = this.getItemData(params);
                const effectiveDate = get(itemData, 'overridePrice.effectiveDate');
                const expiryDate = get(itemData, 'overridePrice.expiryDate');
                if (!effectiveDate || !expiryDate) return null;
                return { start: effectiveDate, end: expiryDate, dateHasChanged: true };
              },
              comparator: agGridUtils.utils.createTimestampComparator(
                `${prefix}overridePrice.expiryTimestamp`
              ),
              context: {
                tsgKey: tsgKey || null,
                visibilityGroup: `referencePrice::${tsgKey || 'none'}`,
              },
              cellClass: params => {
                const itemData = this.getItemData(params);
                const classes = [
                  'ag-right-aligned-cell',
                  'proposed-pricing-cell',
                  this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
                ];

                // Add visible class if we have a date range
                if (get(itemData, 'overridePrice.expiryDate')) {
                  classes.push(
                    'date-range-visible',
                    get(itemData, 'hasUnsavedKpiResults', false) && get(itemData, 'hasPriceChanged')
                      ? 'unsaved-kpis'
                      : ''
                  );
                }

                return classes;
              },
              // Display the expiry date only
              valueFormatter: this.formatExpiryDate,
              tooltipValueGetter: params => {
                const itemData = this.getItemData(params);
                if (
                  !params.context.componentParent.isPriceOverrideEditable ||
                  this.lockedPricesByTsgKey[params.colDef.context.tsgKey]
                ) {
                  return null;
                }

                // date range will be editable once override is created, show tooltip
                if (!params.context.componentParent.hasOverride(itemData)) {
                  return this.$t('gridView.tooltips.dateRangeRequiresOverride');
                }
                return null;
              },
              // Enable editing with our date picker
              editable: params => {
                const itemData = this.getItemData(params);
                return (
                  params.context.componentParent.isPriceOverrideEditable &&
                  !this.lockedPricesByTsgKey[params.colDef.context.tsgKey] &&
                  params.context.componentParent.hasOverride(itemData)
                );
              },
              cellEditor: 'datePickerEditor',
              cellEditorPopup: true,
              cellEditorPopupPosition: 'under',
              cellEditorParams: this.getDatePickerEditorParams,
              onCellClicked: params => {
                params.api.startEditingCell({
                  rowIndex: params.rowIndex,
                  colKey: params.column.getColId(),
                });
              },
              // Process the edited value
              valueSetter: this.handleEffectiveDateValueSetter,
              // Handle changes to the date range
              onCellValueChanged: this.handleEffectiveDateCellValueChanged,
            },
            {
              headerName: this.$t('pricing.scenarioPriceChange'),
              headerClass: params => [
                'proposed-pricing-header-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
              ],
              field: `${prefix}${this.getPriceField('scenarioPriceChange')}`,
              colId: `${prefix}scenarioPriceChange`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              context: {
                tsgKey: tsgKey || null,
              },
              cellClass: params => [
                'ag-right-aligned-cell',
                'proposed-pricing-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
              ],
              valueFormatter: params => {
                const displayValue = this.getPerUnitValueOrBase(params.data, params.colDef.field);
                return this.formatNumberEmptyCheck(
                  { value: displayValue },
                  'value',
                  numberFormats.priceFormat,
                  true
                );
              },
            },
            {
              headerName: this.$t('pricing.scenarioPriceChangeRatio'),
              headerClass: params => [
                'proposed-pricing-header-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
              ],
              field: `${prefix}${this.getPriceField('scenarioPriceChangeRatio')}`,
              colId: `${prefix}scenarioPriceChangeRatio`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              context: {
                tsgKey: tsgKey || null,
              },
              cellClass: params => [
                'ag-right-aligned-cell',
                'proposed-pricing-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
              ],
              valueFormatter: params =>
                this.formatNumberEmptyCheck(params, 'value', numberFormats.percent, true),
            },
            {
              headerName: this.$t('pricing.scenarioMargin'),
              headerClass: params => [
                'proposed-pricing-header-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
              ],
              field: `${prefix}scenarioMargin`,
              colId: `${prefix}scenarioMargin`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              context: {
                tsgKey: tsgKey || null,
              },
              cellClass: params => [
                'ag-right-aligned-cell',
                'proposed-pricing-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
                'font-weight-bold',
              ],
              valueFormatter: params =>
                this.formatNumberEmptyCheck(params, 'value', numberFormats.percent),
            },
            {
              headerName: this.$t('pricing.scenarioSalesImpact'),
              headerClass: params => [
                'proposed-pricing-header-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
              ],
              field: `${prefix}${
                this.showRegularImpact ? 'regularSalesImpact' : 'scenarioSalesImpact'
              }`,
              colId: `${prefix}scenarioSalesImpact`,
              sort: this.isUnitManagerView ? 'desc' : undefined,
              sortIndex: this.isUnitManagerView ? 0 : undefined,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              comparator: this.absoluteValueComparator,
              cellDataType: 'number',
              context: {
                tsgKey: tsgKey || null,
              },
              cellClass: params => {
                return [
                  this.formatNumberColourClass(params.value ? params.value / 1000 : 0),
                  'ag-right-aligned-cell',
                  'proposed-pricing-cell',
                  this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
                  'font-weight-bold',
                ];
              },
              valueFormatter: params =>
                params.value
                  ? this.formatNumber({
                      number: params.value / 1000,
                      format: numberFormats.oneDecimalPlace,
                    })
                  : '-',
            },
            {
              headerName: this.$t('pricing.scenarioCostImpact'),
              headerClass: params => [
                'proposed-pricing-header-cell',
                this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
              ],
              field: `${prefix}${
                this.showRegularImpact ? 'regularCostImpact' : 'scenarioCostImpact'
              }`,
              colId: `${prefix}scenarioCostImpact`,
              filter: 'agMultiColumnFilter',
              filterParams: agGridUtils.getMultiNumericFilterParams(),
              cellDataType: 'number',
              context: {
                tsgKey: tsgKey || null,
              },
              cellClass: params => {
                return [
                  this.formatNumberColourClass(params.value ? params.value / 1000 : 0, true),
                  'ag-right-aligned-cell',
                  'proposed-pricing-cell',
                  this.lockedPricesByTsgKey[params.colDef.context.tsgKey] ? 'disabled' : '',
                  'font-weight-bold',
                ];
              },
              valueFormatter: params =>
                params.value
                  ? this.formatNumber({
                      number: -params.value / 1000,
                      format: numberFormats.oneDecimalPlace,
                    })
                  : '-',
            },
          ],
        },
        {
          headerName: this.$t('pricing.mainHeaders.competitors'),
          marryChildren: true,
          children: competitorColumns,
        },
        {
          headerName: this.$t('pricing.mainHeaders.tension'),
          marryChildren: true,
          children: [
            {
              headerName: this.$t(this.tensionLabel),
              width: 72,
              sortable: false,
              suppressHeaderMenuButton: true,
              colId: `${prefix}tension`,
              cellRendererSelector: params => {
                return get(params.data, `${prefix}scenarioPrice`)
                  ? { component: 'agGridCellRendererComponentWrapper' }
                  : '';
              },
              valueGetter: params =>
                get(params.data, `${prefix}scenarioPrice`)
                  ? {
                      component: 'tension-chart',
                      props: {
                        options: this.tensionChartOptions,
                        scenarioPrice: get(params.data, `${prefix}scenarioPrice`),
                        economicReferencePrice: get(params.data, `${prefix}economicReferencePrice`),
                        competitorReferencePrice: get(
                          params.data,
                          `${prefix}competitorReferencePrice`
                        ),
                        architectureReferencePrice: get(
                          params.data,
                          `${prefix}architectureReferencePrice`
                        ),
                        storeGroupReferencePrice: get(
                          params.data,
                          `${prefix}storeGroupReferencePrice`
                        ),
                        minMaxReferencePrice: get(params.data, `${prefix}minMaxReferencePrice`),
                      },
                    }
                  : '',
            },
          ],
        },
      ];

      // add Alerts column per each TSG when group TSGs mode is enabled
      if (this.isGroupTSGsEnabled) {
        columns.splice(5, 0, {
          headerName: this.$t('pricing.alerts'),
          marryChildren: true,
          colId: `${prefix}alerts`,
          children: [this.getAlertsColumn({ prefix, tooltipPosition: 'top-left' })],
        });
      }

      if (this.isFeatureFlagEnabled(displayDistributionByAssortmentRole)) {
        const children = Object.values(assortmentRoles).map(role => ({
          headerName: this.$t(`pricing.assortmentRoles.${role}`),
          field: `${prefix}distributionByAssortmentRole.${role}.distribution`,
          colId: `${prefix}distributionByAssortmentRole.${role}`,
          tooltipValueGetter: this.getTooltipValueGetter({
            dataPath: `distributionByAssortmentRole.${role}`,
            tooltipFields: {
              'gridView.popupText.previousDistribution': { path: 'previousDistribution' },
            },
          }),
        }));
        children.push({
          headerName: this.$t('pricing.assortmentRoles.changingAssortment'),
          field: `${prefix}distributionByAssortmentRole.changingAssortment`,
          colId: `${prefix}distributionByAssortmentRole.changingAssortment`,
          valueGetter: params => {
            const distribution = get(params.data, `${prefix}distributionByAssortmentRole`, {});
            // changing assortments: furniture holds a changing assortment
            // Assortment roles in Pricing scopes in https://owlabs.atlassian.net/wiki/spaces/RTLS/pages/4921557031/SOK+FactProductPricingStoreGroup
            // During tooldata ingestion in analytics/analytics/client_data/databricks_sql_reader.py get_distribution_by_assortment_role
            // we ingest only the following:
            // dfa.FurnitureAssortmentRole IN ('N', 'X', 'K', 'M', 'T')
            // OR (dfa.FurnitureAssortmentRole LIKE 'F%%' AND dfa.ClientFurnitureArchetypeKey LIKE 'V%%')
            const changingAOKey = Object.keys(distribution).find(key => key.startsWith('F'));
            return changingAOKey ? distribution[changingAOKey].distribution : '';
          },
          tooltipValueGetter: this.getTooltipValueGetter({
            dataPath: `distributionByAssortmentRole`,
            tooltipFields: {
              'gridView.popupText.previousDistribution': distribution => {
                const changingAOKey = Object.keys(distribution).find(key => key.startsWith('F'));
                return changingAOKey ? distribution[changingAOKey].previousDistribution : '';
              },
            },
          }),
        });

        columns.splice(0, 0, {
          headerName: this.$t('pricing.mainHeaders.distribution'),
          marryChildren: true,
          children,
        });
      }

      return columns;
    },

    closeDialog() {
      this.selectedProduct = null;
      this.isProductDialogOpen = false;
      // Row highlight persists after dialog closes (do not clear highlightedProductKey)
    },

    onToggleFilteringByArchitectureGroup(eventData) {
      this.$emit('toggleFilteringByArchitectureGroup', eventData);
    },

    // Custom comparator for absolute value sorting
    absoluteValueComparator(valueA, valueB) {
      const absA = Math.abs(valueA || 0);
      const absB = Math.abs(valueB || 0);
      return absA - absB;
    },
    getTooltipValueGetter({ dataPath, tooltipFields = {}, condition = null }) {
      return params => {
        // Early returns
        if (isEmpty(params.data)) return null;

        const prefixPath = this.getRowPrefix(params);

        // Get data and check conditions
        const data = get(params.data, `${prefixPath}${dataPath}`, {});
        if (isEmpty(data) || (condition && !condition(data))) return null;

        // Format tooltip fields
        const tooltipData = {};

        // Process each tooltip field
        Object.entries(tooltipFields).forEach(([key, config]) => {
          // Get raw value (from function or path)
          const fieldValue = isFunction(config) ? config(data) : get(data, config.path);
          if (isNil(fieldValue) && config.skipIfNull) return;

          // Format the value based on formatter type
          let formattedValue;

          // Apply percent division if required
          const valueToFormat = config.dividePercentBy100
            ? this.formatEngineInput(fieldValue)
            : fieldValue;

          if (!config.format) {
            formattedValue = valueToFormat || config.default || '-';
          } else if (isFunction(config.format)) {
            formattedValue = config.format(valueToFormat);
          } else {
            formattedValue = this.formatNumber({
              number: valueToFormat,
              format: config.format,
              nullAsDash: config.nullAsDash !== false,
              zeroAsDash: config.zeroAsDash === true,
            });
          }

          // Add to result with translated key
          tooltipData[this.$t(key)] = formattedValue;
        });

        return isEmpty(tooltipData) ? null : this.formatTooltipText(tooltipData);
      };
    },
    displayIntentionPriceTooltip(expandableItem) {
      const intentionPriceEffectiveDate = get(expandableItem, 'effectiveDate');
      return (
        this.isFeatureFlagEnabled(useFirstFuturePrice) && !isEmpty(intentionPriceEffectiveDate)
      );
    },

    debouncedFilterChange: debounce(function(newFilter) {
      this.gridApi.setQuickFilter(newFilter);
    }, 800),

    onColumnVisible(event) {
      // Skip processing if we're already updating column visibility
      if (this.updatingSyncedColumns) return;

      // Process attribute columns
      this.updateAdditionalAttributes(event.columns);

      // Process synced columns (price columns)
      this.processColumnsWithSameVisibilityGroup(event.column);

      // Always reconcile bulk selection after column visibility changes
      // This ensures selection state stays in sync when TSG columns are hidden/shown
      if (this.isFeatureFlagEnabled(allowBulkPriceOverrides) && this.debouncedReconcileSelection) {
        this.debouncedReconcileSelection();
      }
    },

    async onLayoutApplied({ columnState }) {
      // Handle layout changes for grid-view - update column state and refetch if needed

      // Check if there are existing tool store group or chain filters that should override the layout
      const { hasToolStoreGroupFilter, hasChainFilter } = hasToolStoreGroupOrChainFilters(
        this.retailAttributesFilter
      );

      let effectiveColumnState = columnState;

      if (hasToolStoreGroupFilter || hasChainFilter) {
        // Apply tool store group filter priority over layout, using the layout's columnState as base
        const updatedColumnState = await this.updateColumnStateForToolStoreGroupFilter({
          levelEntryKey: this.levelEntryKey,
          columnState,
        });

        // Use the updated column state if it was returned, otherwise use the original
        if (updatedColumnState) {
          effectiveColumnState = updatedColumnState;
        }
      }

      // Always call updateColumnStateWithOptionalRefetch with the effective column state
      await this.updateColumnStateWithOptionalRefetch({
        levelEntryKey: this.levelEntryKey,
        isUnitManagerView: this.isUnitManagerView,
        columnState: effectiveColumnState,
      });

      // Reconcile bulk selection after layout changes (which affect column visibility)
      if (this.isFeatureFlagEnabled(allowBulkPriceOverrides) && this.debouncedReconcileSelection) {
        this.debouncedReconcileSelection();
      }
    },

    // when columns chooser is closed
    handleColumnsPanelClosed() {
      // Handle attribute columns
      if (this.additionalAttributes.size !== 0) {
        this.hideAttributeColumnsOverMaxCount();
        // Check if we need to fetch additional attributes
        const hasAddedAttributes = Array.from(this.additionalAttributes).some(
          attributeKey => !this.getGridDisplayedAttributes.has(attributeKey)
        );
        if (hasAddedAttributes) {
          this.setGridDisplayedAttributes(new Set(this.additionalAttributes));
        }
      }

      // Handle competitor columns: track visible competitors
      const visibleCompetitorEntries = new Set();
      this.gridApi.getAllGridColumns().forEach(column => {
        if (column.colId.includes('competitors.') && column.visible) {
          if (this.isGroupTSGsEnabled) {
            // colId format: toolStoreGroups.${tsgKey}.competitors.${competitorKey}
            const [, tsgKey, , competitorKeyRaw] = column.colId.split('.');
            const competitorKey = Number(competitorKeyRaw);
            if (tsgKey && competitorKey) {
              // Only track if not globally visible
              const isGloballyVisible = this.selectedCompetitors.some(
                c => c.competitorKey === competitorKey
              );
              if (!isGloballyVisible) {
                visibleCompetitorEntries.add(`${tsgKey}::${competitorKey}`);
              }
            }
          } else {
            // colId format: competitors.${competitorKey}.${columnName}
            const parts = column.colId.split('.');
            if (parts.length >= 3) {
              const competitorKey = Number(parts[1]);
              if (competitorKey) {
                visibleCompetitorEntries.add(competitorKey);
              }
            }
          }
        }
      });

      // Always update the store, but only refetch if new columns are added
      this.setVisibleCompetitors(visibleCompetitorEntries);

      // Always update the column state in the grid-view store for pick parameter
      const gridColumnState = this.gridApi.getColumnState();
      this.updateColumnStateWithOptionalRefetch({
        levelEntryKey: this.levelEntryKey,
        isUnitManagerView: this.isUnitManagerView,
        columnState: gridColumnState,
      });

      // Reconcile bulk selection after column panel changes (TSG columns may have been hidden/shown)
      if (this.isFeatureFlagEnabled(allowBulkPriceOverrides) && this.debouncedReconcileSelection) {
        this.debouncedReconcileSelection();
      }
    },

    getItemData(params) {
      const tsgKey = params.colDef.context.tsgKey;
      return tsgKey ? get(params.data, `toolStoreGroups.${tsgKey}`) : params.data;
    },

    /**
     * Returns true when a TSG's scenario price matches its chain-level scenario price
     * and no price override is active. Used to suppress redundant per-TSG prices in the grid.
     *
     * @param {object} params - AG Grid cell params
     * @param {string} tsgKey - The tool store group key for the column
     * @returns {boolean}
     */
    isTsgPriceMatchingChain(params, tsgKey) {
      if (!tsgKey || !this.isGroupTSGsEnabled) return false;

      const chainTsgKey = this.chainTsgKeyByTsgKey[tsgKey];
      if (!chainTsgKey || chainTsgKey === tsgKey) return false;

      const itemData = this.getItemData(params);
      if (!itemData) return false;

      // Always show price when an active override is in use
      if (
        params.context.componentParent.hasOverride(itemData) &&
        params.context.componentParent.useOverridePrice(itemData)
      ) {
        return false;
      }

      const chainItemData = get(params.data, `toolStoreGroups.${chainTsgKey}`);
      if (!chainItemData) return false;

      const tsgPrice = get(itemData, this.getPriceField('scenarioPrice'));
      const chainPrice = get(chainItemData, this.getPriceField('scenarioPrice'));

      return tsgPrice === chainPrice;
    },

    // Scenario Price Column Callbacks
    scenarioPriceCellEditorSelector(params) {
      const itemData = this.getItemData(params);

      // Get the override price from the parent component (if one exists)
      const overridePrice = params.context.componentParent.getOverridePrice(itemData);

      // Get the standard scenario price directly from the data object
      const scenarioPrice = get(itemData, this.getPriceField('scenarioPrice'));

      // Determine which price value to edit based on business rules:
      // - If an override price is active and should be used, use that
      // - Otherwise use the standard scenario price
      const actualValue = params.context.componentParent.useOverridePrice(itemData)
        ? overridePrice
        : scenarioPrice;

      // Return configuration for AG Grid's number editor component
      return {
        component: 'CustomNumberCellEditor',
        params: {
          value: actualValue,
        },
      };
    },

    handleScenarioPriceCellDoubleClick(params) {
      if (!this.isFeatureFlagEnabled(cancelScenarioPriceWithDoubleClick)) {
        return false;
      }
      // Stop any ongoing editing
      params.api.stopEditing();

      if (this.lockedPricesByTsgKey[params.colDef.context.tsgKey]) {
        // Prevent double-click if tsg prices are locked
        return false;
      }

      const itemData = this.getItemData(params);
      // set referencePriceAtOverride before changeOverridePrice is called
      set(
        itemData,
        'overridePrice.referencePriceAtOverride',
        this.getReferencePriceAtOverride(itemData)
      );
      const intentionPrice = params.context.componentParent.getIntentionPrice(itemData);

      // Only proceed if we have a valid intention price
      if (intentionPrice) {
        params.context.componentParent.recalculateKPIWrapper({
          callback: () =>
            params.context.componentParent.changeOverridePrice(intentionPrice, itemData),
          item: itemData,
          rowNode: params.node,
        });
      } else {
        console.error('intention price missing. cannot change override price');
      }

      // Prevent default cell editing
      return false;
    },

    handleScenarioPriceValueSetter(params) {
      // If no actual input, return false to indicate no change
      if (params.newValue === undefined || params.newValue === null || params.newValue === '') {
        return false;
      }

      // Ensure we have a valid number
      let numericValue = this.formatStringToNumber(params.newValue);
      if (Number.isNaN(numericValue)) {
        return false;
      }

      // Get the current effective value that's showing
      const itemData = this.getItemData(params);

      const scenarioPriceByType = get(itemData, this.getPriceField('scenarioPrice'));
      const overridePrice = params.context.componentParent.getOverridePrice(itemData);
      const currentPrice = params.context.componentParent.useOverridePrice(itemData)
        ? overridePrice
        : scenarioPriceByType;

      // If the value hasn't actually changed, reject the edit
      if (Math.abs(numericValue - currentPrice) < 0.0001) {
        return false;
      }

      // N.B. referencePriceAtOverride needs to be set before scenarioPrice is updated
      set(
        itemData,
        'overridePrice.referencePriceAtOverride',
        this.getReferencePriceAtOverride(itemData)
      );

      // Stage the converted price for onCellValueChanged to pick up.
      // Do NOT mutate scenarioPrice — it is the engine-calculated baseline and must
      // remain intact so that revert can display the correct pre-override price.
      numericValue = params.context.componentParent.convertPriceToRegular(itemData, numericValue);
      set(itemData, '_pendingOverridePrice', numericValue);
      // Capture current row order and freeze sort positions until the user deliberately
      // re-applies sort. postSortRows uses stableSortRowOrder to restore positions across
      // all columns, not just the scenario price column.
      this.captureStableSortRowOrder();
      return true;
    },

    handleScenarioPriceCellValueChanged(params) {
      // Read the staged override price from handleScenarioPriceValueSetter.
      // We use _pendingOverridePrice instead of scenarioPrice because scenarioPrice
      // is the engine baseline — mutating it would break the revert display.
      const itemData = this.getItemData(params);
      const numericValue = get(itemData, '_pendingOverridePrice');
      set(itemData, '_pendingOverridePrice', undefined);

      // Only process if we have a valid numeric value and it's actually changed
      if (numericValue !== undefined && numericValue !== null && !Number.isNaN(numericValue)) {
        params.context.componentParent.recalculateKPIWrapper({
          callback: () =>
            params.context.componentParent.changeOverridePrice(numericValue, itemData),
          item: itemData,
          rowNode: params.node,
        });
      }
    },

    // Effective Date Range Column Callbacks
    /**
     * Builds the date picker editor params for the effectiveDateRange column.
     * Passes in the current effective and expiry dates as the initial range value,
     * and wires up the remove lock action when a price lock is active.
     * @param {object} params - AG Grid cell editor params.
     * @returns {object} Date picker editor configuration.
     */
    getDatePickerEditorParams(params) {
      const itemData = this.getItemData(params);
      // Get existing dates from the data
      const effectiveDate = get(itemData, 'overridePrice.effectiveDate');
      const expiryDate = get(itemData, 'overridePrice.expiryDate');

      return {
        range: true,
        minDate: () => moment().format(yearMonthDayFormat),
        firstDayOfWeek: this.i18nconfig.firstDayOfTheWeek,
        locale: this.i18nconfig.fallbackLocale,
        showRemoveLockButton: true,
        autoSelectToday: true,
        removeLockButtonText: this.$t('gridView.removePriceLock'),
        value: effectiveDate && expiryDate ? [effectiveDate, expiryDate] : [],
        actions: params.context.componentParent.showRemoveLockButton(itemData)
          ? [
              {
                label: params.context.componentParent.getRemoveLockButtonText(),
                callback: () =>
                  params.context.componentParent.handleRemovePriceLock(params, itemData),
              },
            ]
          : [],
      };
    },

    /**
     * Value setter for the effectiveDateRange column.
     * Accepts a { start, end, dateHasChanged } range object from the date picker or fill handle.
     * Guards against fill handle / paste applying dates to rows that do not already have an override.
     * @param {object} params - AG Grid value setter params.
     * @returns {boolean} True if the value was updated, false otherwise.
     */
    handleEffectiveDateValueSetter(params) {
      if (
        !params.newValue ||
        !params.newValue.start ||
        !params.newValue.end ||
        !params.newValue.dateHasChanged
      ) {
        return false;
      }

      const itemData = this.getItemData(params);

      // Guard: do not create or update dates if no price override exists.
      // This prevents fill handle and paste from setting dates on rows that
      // have not been explicitly overridden.
      if (isNil(get(itemData, 'overridePrice.price'))) {
        return false;
      }

      set(itemData, 'overridePrice.effectiveDate', params.newValue.start);
      set(itemData, 'overridePrice.effectiveTimestamp', moment(params.newValue.start).unix());
      set(itemData, 'overridePrice.expiryDate', params.newValue.end);
      set(itemData, 'overridePrice.expiryTimestamp', moment(params.newValue.end).unix());
      set(itemData, 'overridePrice.isSmart', true);

      return true;
    },

    handleEffectiveDateCellValueChanged(params) {
      const itemData = this.getItemData(params);
      const intentionPrice = params.context.componentParent.getIntentionPrice(itemData);
      const usePromoPriceOnSmartOverride = params.context.componentParent.useLockedPrice(itemData);

      // Batch fill handle date changes into a single updateOverridePrice({ items }) call.
      // pendingDateFillItems is set to an array on fillStart and flushed on fillEnd.
      if (Array.isArray(this.pendingDateFillItems)) {
        // can't set date without existing override, so at this point we know override exists and
        // KPIs are stale. Mark directly on the actual data object so cellClass reads it immediately.
        // note it may not be necessary to recalculate KPIs, but we do need this to mark as unsaved
        // in databricks. ideally we would decouple these 2 states.
        itemData.hasUnsavedKpiResults = true;
        this.pendingDateFillItems.push({
          item: {
            ...itemData,
            startingPrice: intentionPrice,
            usePromoPriceOnSmartOverride,
            hasUnsavedKpiResults: true,
          },
          rowNode: params.node,
        });
        return;
      }

      // Update the override price on the server
      params.context.componentParent.recalculateKPIWrapper({
        callback: () =>
          params.context.componentParent.updateOverridePrice({
            item: { ...itemData, startingPrice: intentionPrice, usePromoPriceOnSmartOverride },
          }),
        item: itemData,
        rowNode: params.node,
      });
    },

    formatEffectiveDateRange(params) {
      const itemData = this.getItemData(params);
      const effectiveDate = get(itemData, 'overridePrice.effectiveDate');
      const expiryDate = get(itemData, 'overridePrice.expiryDate');

      if (!effectiveDate || !expiryDate) {
        return '';
      }

      return `${moment(effectiveDate).format(monthDayYearFullSlashesFormat)} - ${moment(
        expiryDate
      ).format(monthDayYearFullSlashesFormat)}`;
    },

    formatExpiryDate(params) {
      const itemData = this.getItemData(params);
      const expiryDate = get(itemData, 'overridePrice.expiryDate');

      if (!expiryDate) {
        return '';
      }

      return moment(expiryDate).format(monthDayYearFullSlashesFormat);
    },

    getPerUnitValueOrBase(data, field) {
      const value = get(data, field);

      // When per-norm-weight value is 0 due to missing normWeight, show base value instead
      if (value === 0 && field.includes('PerNormWeight') && !get(data, 'normWeight')) {
        return get(data, field.replace('PerNormWeight', ''));
      }

      return value;
    },

    async selectScenario() {
      // Initialise layouts if not already set (e.g. after a scenario switch)
      if (!this.activeLayout) {
        const { layouts: fetchedLayouts, activeLayout } = await this.initLayoutsAndSetActive({
          gridType: gridTypes.GRIDVIEW,
        });

        // Auto-activate the first available layout when the user has no saved preference
        if (!activeLayout && fetchedLayouts.length > 0) {
          await this.$store.dispatch('gridLayouts/setActiveLayoutAndSave', {
            layout: fetchedLayouts[0],
            gridType: gridTypes.GRIDVIEW,
          });
        } else if (!activeLayout && fetchedLayouts.length === 0) {
          // No layouts exist at all — inform the user to save their first layout
          this.noLayoutsExist = true;
        }
      }

      // Always fetch fresh data for the newly selected scenario
      await this.fetchItems({
        levelEntryKey: this.levelEntryKey,
        isUnitManagerView: this.isUnitManagerView,
      });
    },

    /* -------------------------------------------------- *
     *           Column visibility helper functions       *
     * -------------------------------------------------- */

    // Sync columns based on visibilityGroup in context
    processColumnsWithSameVisibilityGroup(column) {
      if (!column || !this.gridApi) return;

      try {
        this.updatingSyncedColumns = true;

        const columnDef = column.getColDef();
        const context = columnDef.context || {};
        const newVisibility = column.isVisible();
        const visibilityGroup = context.visibilityGroup;

        if (visibilityGroup) {
          this.syncColumnsWithSameVisibilityGroup(col => {
            const colContext = col.getColDef().context || {};
            return colContext.visibilityGroup === visibilityGroup;
          }, newVisibility);
        }
      } finally {
        this.updatingSyncedColumns = false;
      }
    },

    // Sync columns based on a match function
    syncColumnsWithSameVisibilityGroup(matchFunction, newVisibility) {
      const columnsToSync = this.gridApi.getAllGridColumns().filter(matchFunction);

      if (columnsToSync.length) {
        this.gridApi.setColumnsVisible(columnsToSync, newVisibility);
      }
    },

    getContextMenuItems(params) {
      const itemsToRemove = ['separator', 'export'];
      const itemsToInclude = ['excelExport'];

      const filteredItems = difference(params.defaultItems, itemsToRemove);

      return union(filteredItems, itemsToInclude);
    },

    /**
     * Exports grid data with added columns for alerts and tension
     */
    exportGridWithCustomSettings() {
      exportGridWithCustomColumns({
        gridApi: this.gridApi,
        gridColumnApi: this.gridColumnApi,
        getItemData: this.getItemData,
        alertTypes: this.alertTypes,
        translate: this.$t.bind(this), // Bind $t to component context
        fileName: 'workpackage_product_data.xlsx',
        columnsToExclude: ['weekPriceChange', 'tension', 'scenarioSalesImpact'],
        selectedHistoricalPeriod: this.selectedHistoricalPeriod,
        isFeatureFlagEnabled: this.isFeatureFlagEnabled,
        formatEffectiveDateRange: this.formatEffectiveDateRange,
        formatNumber: this.formatNumber,
      });
    },

    /**
     * Downloads current view - called by global download button
     * Uses local export functionality or shows competitor selection modal
     */
    downloadCurrentView() {
      // If enabled, display modal for selecting competitors for download
      if (this.isFeatureFlagEnabled(selectCompetitorsOnExport)) {
        this.isSelectCompetitorsDialogOpen = true;
      } else {
        // If disabled, use local export functionality directly
        this.exportGridWithCustomSettings();
      }
    },

    /**
     * Downloads data after competitor selection from modal
     */
    downloadDataWithSelection(event) {
      this.$emit('downloadData', event);
      this.isSelectCompetitorsDialogOpen = false;
    },

    /**
     * Returns the correct visibility object for column based on FF
     * - If feature flag is disabled: always hide
     * - If enabled: if activeLayout, let layout control; else, show
     */
    // Hides columns gated by a feature flag; visible columns are controlled by the layout's column state
    getColumnVisibilityState(featureFlag) {
      return this.isFeatureFlagEnabled(featureFlag) ? {} : { hide: true };
    },

    getCostDeltaPercentage(params, prefix) {
      const costDelta = get(params, `data.${prefix}${this.getPriceField('costDelta')}`);
      const nonPromoNetCost = get(params, `data.${prefix}${this.getPriceField('nonPromoNetCost')}`);
      if (isNil(costDelta) || isNil(nonPromoNetCost)) {
        return null;
      }
      // percentage formatter handles multiplication by 100, so return as a decimal
      return costDelta / nonPromoNetCost;
    },

    fillOperation(params) {
      // mod to get remainder. e.g. if there are 2 values, alternate between them apply to each cell in selection
      const fillValue = params.initialValues[params.currentIndex % params.initialValues.length];
      if (!fillValue) return false;
      return fillValue;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';
@import '@style/mixins/_general.scss';

.controls-row {
  flex-wrap: nowrap !important;

  &__divider {
    margin: 14px 0;
  }

  ::v-deep .v-btn {
    letter-spacing: normal;
  }
}

.filter-by-text {
  overflow: hidden;
  color: var(--text-icon-color-text-primary, $text-color-dark);
  text-overflow: ellipsis;
  font-family: 'Noto Sans';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
}

.filter-buttons-container {
  flex-shrink: 0;
}

.engine-controls-col {
  min-width: 0;
  overflow: hidden;
}

.filter-col {
  position: sticky;
  right: 0;
  background: $pricing-white;
  z-index: 5;
}

// Main Variables & Mixins
// Use existing variables from _variables.scss

// Nested styles using SCSS for AG Grid Component
::v-deep {
  .ag-product-list-grid {
    /* ==========================================
       Header Styles
       Controls the appearance of column headers
       ========================================== */
    &__header,
    .ag-header-cell {
      .ag-cell-label-container {
        flex-direction: column-reverse;
        align-items: start;

        .ag-header-cell-menu-button {
          margin-left: -2px;
        }

        .ag-header-cell-text {
          font-weight: bold;
        }
      }
    }

    /* ==========================================
       Header Group Styles
       Styles for grouped headers in the grid
       ========================================== */
    .ag-header-group-cell {
      border-left: 1px solid $custom-ag-grid-header-separator-color;

      .ag-header-cell-resize::after {
        visibility: hidden;
      }

      &:last-child {
        border-right: 1px solid $custom-ag-grid-header-separator-color;
      }

      &-first {
        border-left: 1px solid $custom-ag-grid-header-separator-color;
      }

      &-last {
        &:last-child {
          border-right: 1px solid $custom-ag-grid-header-separator-color;
        }

        .ag-header-cell-resize::after {
          visibility: hidden;
        }
      }
    }

    /** Prevent double borders when columns are pinned */
    .ag-pinned-left-header {
      .ag-header-group-cell:last-child,
      .ag-header-group-cell-last:last-child {
        border-right: none;
      }
    }

    .ag-pinned-right-header {
      .ag-header-group-cell:first-child,
      .ag-header-group-cell-last:first-child {
        border-left: none;
      }
    }

    /* ==========================================
       Proposed Pricing Styles
       Special styling for pricing-related cells
       ========================================== */
    %proposed-pricing-shared {
      background: $grid-view-scenario-blue;
      border-color: $pricing-primary;
      border-width: 2px;

      &:hover {
        background: $grid-view-scenario-blue !important;
      }

      &.disabled {
        background: $grid-view-row-disabled;
        opacity: 0.5;
      }
    }

    .proposed-pricing {
      &-header {
        &-cell {
          @extend %proposed-pricing-shared;
        }

        &-group-cell {
          @extend %proposed-pricing-shared;
        }
      }
    }

    /* ==========================================
       Cell Styles
       Base styles for grid cells
       ========================================== */
    .ag-cell {
      display: inline-block;
      cursor: pointer;
      color: rgba(0, 0, 0, 0.87);
      border-bottom: none;
      border-top: none;

      &.ag-group-cell {
        &-first {
          border-left: 1px solid $custom-ag-grid-header-separator-color;
        }
      }

      &.ag-column-last {
        border-right: 1px solid $custom-ag-grid-header-separator-color;
      }

      &.proposed-pricing-cell {
        background: $grid-view-scenario-blue;

        &.disabled {
          background: $grid-view-row-disabled;
          opacity: 0.5;
        }

        &.ag-group-cell-first {
          border-left-color: $pricing-primary;
          border-left-width: 2px;
        }
      }

      &.font-weight-bold {
        font-weight: bold;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
    }

    .ag-cell.unsaved-kpis {
      background-color: $pricing-orange-light !important;
    }

    .ag-cell.validation-failure-cell {
      background-color: $grid-view-row-validation-failure-color !important;
    }

    /* ==========================================
       Row Styles
       Controls the appearance of grid rows
       ========================================== */
    .ag-row {
      border-bottom: none;
      border-top: none;

      &.ag-row-even {
        background: $custom-ag-grid-even-rows-background-color;

        .ag-cell.proposed-pricing-cell {
          background: $grid-view-scenario-dense-blue;

          &.disabled {
            background: color-mix(in srgb, $grid-view-row-disabled, white 60%);
            opacity: 0.5;
          }
        }
      }

      &.ag-row-odd {
        background: $custom-ag-grid-odd-rows-background-color;
      }

      &.promotion-row {
        &.ag-row-even {
          background: $grid-view-row-promo-dense-color !important;
        }

        &.ag-row-odd {
          background: $grid-view-row-promo-color !important;
        }

        .ag-cell.proposed-pricing-cell {
          background: inherit;
        }

        &:hover {
          background-color: $grid-view-row-promo-dense-hover-color !important;

          .ag-cell.proposed-pricing-cell {
            background: $grid-view-row-promo-dense-hover-color !important;
          }
        }
      }

      &:not(.promotion-row):hover {
        background-color: $grid-view-hover-color !important;

        .ag-cell.proposed-pricing-cell {
          background: $grid-view-hover-color !important;
        }

        .ag-cell.proposed-pricing-cell.validation-failure-cell {
          background: darken($grid-view-row-validation-failure-color, 5%) !important;
        }
      }

      &.highlighted-row {
        outline: 1px solid $pricing-dark-blue;
        outline-offset: -1px;

        &.ag-row-even {
          // Fallback for browsers without color-mix() support (Chrome <111, Firefox <113, Safari <16.2)
          background: $custom-ag-grid-highlighted-even-rows-background-color-fallback !important;
          background: color-mix(
            in srgb,
            $pricing-primary 12%,
            $custom-ag-grid-even-rows-background-color
          ) !important;
        }

        &.ag-row-odd {
          // Fallback for browsers without color-mix() support (Chrome <111, Firefox <113, Safari <16.2)
          background: $custom-ag-grid-highlighted-odd-rows-background-color-fallback !important;
          background: color-mix(
            in srgb,
            $pricing-primary 12%,
            $custom-ag-grid-odd-rows-background-color
          ) !important;
        }
      }

      .ag-input {
        padding-left: 4px;
        text-align: right;
        padding-right: 8px;
      }
    }

    /* ==========================================
       Utility Classes
       Reusable utility classes
       ========================================== */
    .alerts-cell {
      display: flex;
      overflow: visible;
      padding: 0 8px 0 4px;
    }

    .cell-resize-invisible {
      .ag-header-cell-resize::after {
        visibility: hidden;
      }
    }

    .ag-side-buttons {
      display: none;
    }
  }
}
/* ==========================================
   Container & Dialog Styles
   Styles for grid container and dialogs
   ========================================== */
.grid-view-ag-grid-content {
  transition: height 0.5s ease-in-out;
  height: 78vh;
  width: 100%;
}

.product-dialog-content,
.close-btn {
  font-size: 1.2rem;
}

/* Add some styling for tooltip text */
::v-deep .ag-tooltip {
  max-width: 350px;
  white-space: pre-wrap;
  line-height: 1.5;
  padding: 5px 10px;
}

/* Add styles for the sidebar toggle button */
.columns-toggle {
  width: auto;
  height: 28px;
}

::v-deep .norm-weight-toggle {
  .theme--light.v-btn-toggle:not(.v-btn-toggle--group) .v-btn.v-btn {
    border-color: $pricing-primary !important;
    opacity: 1;
    font-weight: 500;
  }
}

/* Inline note inside the Columns tool panel */
::v-deep .ag-product-list-grid .ag-column-panel::before {
  @include column-panel-data-load-message;
}
</style>
